import { persistDesktopCredential } from "./desktop-credentials.js";
import { ReadCursorState } from "./read-cursor-state.svelte.js";
import { TypingLines } from "./typing-lines.svelte.js";
import {
  countsAsThreadReply,
  rootWithReply,
  type LiveReply,
  type ReplyAgents,
  type ReplyCybos,
} from "../thread-reply.js";
import { toast } from "svelte-sonner";
import { isBridgedAuthor } from "$lib/utils.js";
import { SlackClient } from "./client.js";
import type { ThreadCounts, ThreadSummary } from "./client.js";
import { saveRefreshToken, clearRefreshToken } from "./auth-refresh.js";
import { SESSION_KEY } from "./saved-session.js";
import { workspaceListFromAuth } from "./workspace-list.js";
import { timeoutSignal } from "./abort-signal.js";
import { isMe } from "$lib/session-scope.js";
import {
  compareMessages,
  findOptimisticIndex,
  insertMessageSorted,
  isSettledDuplicate,
  pageTailId,
  replaceSorted,
  withSendStatus,
} from "./message-reconcile.js";
import {
  applyStatusChange,
  epochMsToIso,
  isLiveStatus,
  nextStatusExpiry,
  pruneExpiredStatuses,
  reconcileStatusSnapshot,
  type MemberStatus,
} from "./status-reconcile.js";
import type {
  CyborgUser,
  Workspace,
  Channel,
  Message,
  Task,
  TypingEvent,
  WorkspaceMember,
  ExternalParticipant,
} from "./types.js";

// ─── State Classes ────────────────────────────────────────────────

export class WorkspaceState {
  current: Workspace | null = $state(null);
  list: Workspace[] = $state([]);
  // True once sign-in has hydrated `list` (even to zero memberships); false while
  // signed out or before the auth answer lands. An empty list means "none" only then.
  listLoaded = $state(false);
  channels: Channel[] = $state([]);
  tasks: Task[] = $state([]);
  // oxlint-disable-next-line typescript-eslint/no-explicit-any -- typed by consumer plugin
  private _agents: any[] = $state([]);

  // ─── Stale-snapshot lifecycle guard ────────────────────────────────────
  //
  // `agents` is replaced WHOLESALE from ~8 call sites (every listAgents refetch,
  // reconnect, workspace select, cybo spawn/archive). A snapshot FETCHED before a
  // turn ended lands AFTER the live push that ended it, re-writing lifecycle
  // "running" over the fresh "idle" — and nothing ever clears it again: the
  // composer's queue drains only on turn_completed or reconcileTurnWithRoster,
  // neither of which can fire for a turn that is already over, and the idle
  // watchdog is armed only by turn_started. That is the "stuck in Queue mode
  // until you restart the app" bug.
  //
  // The guard lives in the SETTER — the one funnel all eight writers already
  // pass through — rather than being copy-pasted into each of them (and it
  // covers writers added later for free). BOUNDED on purpose: a push only
  // outranks a snapshot for LIVE_LIFECYCLE_MS, comfortably longer than a
  // list_agents round trip, so a DROPPED push can never make the client ignore
  // server truth permanently — that would just be this same bug mirrored.
  private liveLifecycle = new Map<string, { lifecycle: string; at: number }>();
  private static readonly LIVE_LIFECYCLE_MS = 15_000;

  // oxlint-disable-next-line typescript-eslint/no-explicit-any -- typed by consumer plugin
  get agents(): any[] {
    return this._agents;
  }

  // oxlint-disable-next-line typescript-eslint/no-explicit-any -- typed by consumer plugin
  set agents(next: any[]) {
    this._agents = this.withLiveLifecycle(next);
  }

  // Record an authoritative LIVE lifecycle for one agent and apply it to that
  // row immediately. The only writers are the daemon's per-agent
  // `cyborg:agent_status` broadcast and the local turn-event edge — both are
  // "this is true right now", unlike a roster snapshot which is only "this was
  // true when the fetch was served".
  noteAgentLifecycle(agentId: string, lifecycle: string): void {
    this.liveLifecycle.set(agentId, { lifecycle, at: Date.now() });
    this._agents = this.withLiveLifecycle(this._agents);
  }

  // oxlint-disable-next-line typescript-eslint/no-explicit-any -- typed by consumer plugin
  private withLiveLifecycle(rows: any[]): any[] {
    if (this.liveLifecycle.size === 0) return rows;
    const cutoff = Date.now() - WorkspaceState.LIVE_LIFECYCLE_MS;
    let changed = false;
    const next = rows.map((row) => {
      const live = this.liveLifecycle.get(row?.agentId);
      if (!live) return row;
      if (live.at < cutoff) {
        this.liveLifecycle.delete(row.agentId);
        return row;
      }
      if (row.lifecycle === live.lifecycle) return row;
      changed = true;
      return { ...row, lifecycle: live.lifecycle };
    });
    // Same array reference when nothing moved, so an unrelated roster write
    // doesn't invalidate every subscriber that only reads lifecycle.
    return changed ? next : rows;
  }

  members: WorkspaceMember[] = $state([]);
  // Bridge guests who posted here (never seats): what a mention of one resolves against.
  external: ExternalParticipant[] = $state([]);

  // Read-repair lookup (#4102 follow-up): a bridge guest's synthetic id → the workspace member
  // it is a VERIFIED link of. A member who posted in a linked Slack channel before linking was
  // mirrored under a `slack:` author, which persists; this lets ChatMessage render that stale
  // byline as the member ("via Slack") instead of the external-guest pill. Memoized on the
  // `external` array identity (rebuilt only on a roster write), so every message shares one map.
  #bridgeLinksSrc: ExternalParticipant[] | null = null;
  #bridgeLinks = new Map<string, string>();
  get bridgedMemberLinks(): Map<string, string> {
    if (this.#bridgeLinksSrc !== this.external) {
      this.#bridgeLinksSrc = this.external;
      this.#bridgeLinks = new Map(
        this.external
          .filter((e) => !!e.linkedMemberId)
          .map((e) => [e.userId, e.linkedMemberId as string] as const),
      );
    }
    return this.#bridgeLinks;
  }
  // Switching to another workspace (rail click): true while selectWorkspace loads
  // the target's data, so the shell can show a clear indicator instead of a blank
  // half-loaded view. switchError holds a message when the load fails (so the UI
  // shows an error + retry instead of an infinite spinner).
  switching = $state(false);
  switchError: string | null = $state(null);
  // Members load asynchronously AFTER channels in selectWorkspace, so there's a
  // real window where channels exist but the DM roster is still in flight. The
  // sidebar shows skeleton rows for the DM list while this is true (item 4).
  membersLoading = $state(false);
  // Set when a listMembers fetch REJECTS (loadMembers's catch also clears
  // `members` to []). Lets the Members settings tab tell a failed load apart
  // from a genuinely empty workspace — without it, a relay hiccup cleared
  // membersLoading with an empty roster and "No members yet" rendered as if
  // that were true. Cleared on a successful load and at the start of a retry.
  // Mirrors cyboState.loadError (plugins/agents/state.svelte.ts).
  membersLoadError = $state(false);
  // True until the FIRST agent-roster fetch for the current workspace SETTLES.
  // cyborg:list_agents is daemon-forwarded, so it resolves only once the local
  // daemon answers — seconds after the shell paints from relay data. Without this
  // a surface cannot tell "the daemon hasn't answered yet" from "this workspace
  // has no agents". Bounded: it clears even when the fan-out found no live daemon,
  // so nothing waits forever on a dead one.
  agentsLoading = $state(false);
  // True until the FIRST task fetch for the current workspace resolves (or on a
  // reset). Lets the work-items board distinguish "still loading" from "truly
  // empty" so it renders a skeleton, not the "you have no tasks" empty state,
  // during the initial load. Only the first-load state matters — live
  // tasks_changed updates and background reconnect refetches don't flip it.
  tasksLoading = $state(true);
  // Non-null when the FIRST task fetch for the current workspace REJECTED. Lets
  // the work-items board tell a failed load apart from a genuinely empty project
  // — without it, a network failure clears tasksLoading with zero tasks and the
  // board masquerades the failure as the "create your first work item" CTA. Set
  // in selectWorkspace's fetchTasks path, cleared on success and on reset.
  tasksError = $state<string | null>(null);

  // ─── Channel preview (read a public channel without joining) ───────
  // `activeChannel` resolves ONLY from the joined `channels` array, so it's null
  // for a non-member. `previewChannel` holds the channel object a user is peeking
  // at read-only. `viewedChannel` is the unified accessor every preview-safe READ
  // should use (header title, empty-state, name lookups); every WRITE-gated bit of
  // UI keeps reading `activeChannel` so it auto-disables in preview. Cleared on
  // join (the channel lands in `channels`, `activeChannel` resolves) and whenever
  // a real joined channel is selected.
  previewChannel: Channel | null = $state(null);

  // knowledge/142: a host's channel opened in place from the External section.
  hostChannel: { workspaceId: string; channel: Channel } | null = $state(null);

  // Where a channel's reads and writes go: its host, or the workspace on screen.
  channelWorkspaceId(channelId: string | null | undefined): string | undefined {
    const host = this.hostChannel;
    return host && host.channel.id === channelId ? host.workspaceId : this.current?.id;
  }

  get openHostChannel(): { workspaceId: string; channel: Channel } | null {
    const host = this.hostChannel;
    return host && host.channel.id === channelState.activeId ? host : null; // null: another channel is open
  }

  get viewingAsExternal(): boolean {
    return !!this.current?.external || this.openHostChannel !== null;
  }

  get activeChannel(): Channel | null {
    const host = this.openHostChannel;
    if (host) return host.channel;
    return this.channels.find((c) => c.id === channelState.activeId) ?? null;
  }

  // The channel currently on screen — the joined one if a member, otherwise the
  // previewed one. Preview-safe reads use this; writes stay on `activeChannel`.
  get viewedChannel(): Channel | null {
    return this.activeChannel ?? this.previewChannel;
  }

  // True only while previewing: not a member of the open channel, but a preview
  // channel is seeded and it matches the active channel id (so a stale preview
  // slot from a prior peek can't make a freshly-joined channel look previewed).
  get isPreviewing(): boolean {
    return (
      this.activeChannel === null &&
      this.previewChannel !== null &&
      this.previewChannel.id === channelState.activeId
    );
  }
}

export class ChannelState {
  // Every channel switch in the app assigns `channelState.activeId` (selectChannel,
  // selectChannelPreview, the DM/agent/leave/delete null-outs), so this setter is
  // the ONE choke point where a switch is observable — which is where the previous
  // channel's typing line gets dropped (clearTyping below). Same belt-and-braces
  // placement as setMessages/prependMessages' channel filter: the guard holds even
  // for a caller that forgets it. Assigning the SAME id is a no-op so a route
  // re-entry can't flicker a live indicator off.
  private _activeId: string | null = $state(null);
  get activeId(): string | null {
    return this._activeId;
  }
  set activeId(id: string | null) {
    if (id === this._activeId) return;
    this._activeId = id;
    this.clearTyping();
  }
  messages: Message[] = $state([]);
  hasMore = $state(false);
  // CYBORG-767: an around-window open loaded read context + the unread head but
  // NOT the tail — true when newer-than-window messages exist. Keeps the
  // open-at-divider state from being mistaken for "caught up at the tail".
  hasMoreNewer = $state(false);
  loading = $state(false);
  readonly #typing = new TypingLines();
  get typing(): TypingEvent[] {
    return this.#typing.typing;
  }

  // ─── Item 12: unread divider (Slack-mirror) ───
  // Frozen `lastReadAt` (epoch ms) captured at channel-open from readCursorState
  // BEFORE the badge clears. The "New messages" divider renders above the first
  // loaded message with `createdAt > unreadCursor && fromId !== me` and does NOT
  // move as new messages arrive or the badge clears. null = no cursor (no divider).
  unreadCursor = $state<number | null>(null);

  setUnreadCursor(ms: number | null): void {
    this.unreadCursor = ms;
  }

  // Another device of mine read further (read_broadcast, or a reconnect seed ahead of
  // this device's own reads): the divider follows the server cursor, forward only.
  advanceUnreadCursor(ms: number): void {
    if (this.unreadCursor !== null && ms > this.unreadCursor) this.unreadCursor = ms;
  }

  // Monotonic workspace-wide sync high-water (#844). lastSeq feeds drainSync's
  // reconnect/seq-gap cursor. Deriving it from the trimmed in-memory window let a
  // 50-row fetch drop the true max, so reconnect re-asked from a low seq and
  // permanently skipped messages. This only ever rises — trimming the window
  // (setMessages) can't lower it.
  private maxAppliedSeq = 0;
  private trackSeq(msgs: Message | Message[]): void {
    const list = Array.isArray(msgs) ? msgs : [msgs];
    for (const m of list) {
      if (!m.id.startsWith("local-") && m.seq > this.maxAppliedSeq) this.maxAppliedSeq = m.seq;
    }
  }

  addMessage(msg: Message): void {
    this.trackSeq(msg);
    if (this.messages.find((m) => m.id === msg.id)) return;
    if (isSettledDuplicate(this.messages, msg)) return;
    const localIdx = findOptimisticIndex(this.messages, msg);
    if (localIdx >= 0) {
      // Reconciled: the server echo replaces the optimistic local row. Carry a
      // transient "sent" marker so the row flashes a check, then clear it so the
      // settled steady state shows no indicator. No clientMsgId fixup needed —
      // CYBORG-801 bars an untagged echo from settling a TAGGED row, so messageKey
      // survives the local→server id swap either way (#msg-remount).
      const settledId = msg.id;
      this.messages = replaceSorted(this.messages, localIdx, { ...msg, sendStatus: "sent" });
      setTimeout(() => this.clearSentMarker(settledId), 1800);
    } else {
      this.messages = insertMessageSorted(this.messages, msg);
    }
  }

  // Drop the transient "sent" check once the reconciled row has settled.
  private clearSentMarker(id: string): void {
    const idx = this.messages.findIndex((m) => m.id === id);
    if (idx < 0 || this.messages[idx].sendStatus !== "sent") return;
    const { sendStatus: _drop, ...rest } = this.messages[idx];
    this.messages[idx] = rest;
    this.messages = this.messages.slice();
  }

  // Update the optimistic send-status of a still-local message (pending → failed,
  // or back to pending on retry). No-op once the row has reconciled to a server id.
  setSendStatus(id: string, status: "pending" | "sent" | "failed", reason?: string): void {
    const idx = this.messages.findIndex((m) => m.id === id);
    if (idx < 0) return;
    this.messages = withSendStatus(this.messages, idx, status, reason);
  }

  // Roll back an optimistic message (e.g. when the send failed) so it doesn't
  // linger forever as a ghost the server will never echo.
  removeMessage(id: string): void {
    this.messages = this.messages.filter((m) => m.id !== id);
  }

  setPinned(messageId: string, pinnedAt: number | null, pinnedBy: string | null): void {
    const idx = this.messages.findIndex((m) => m.id === messageId);
    if (idx < 0) return;
    this.messages[idx] = { ...this.messages[idx], pinnedAt, pinnedBy };
    this.messages = this.messages.slice();
  }

  // Reply ids already counted: the same frame can arrive twice (the daemon's local broadcast and
  // the relay's re-broadcast of its forwarded copy), and the pill must count each reply once.
  private countedReplies = new Set<string>();

  // Bump a parent message's thread indicator when a reply arrives.
  bumpReplyCount(
    parentId: string,
    reply: LiveReply,
    agents?: ReplyAgents,
    cybos?: ReplyCybos,
  ): void {
    if (!countsAsThreadReply(reply)) return;
    if (this.countedReplies.has(reply.id)) return;
    this.countedReplies.add(reply.id);
    if (this.countedReplies.size > 2000) {
      this.countedReplies.delete(this.countedReplies.values().next().value as string);
    }
    const idx = this.messages.findIndex((m) => m.id === parentId);
    if (idx < 0) return;
    this.messages[idx] = rootWithReply(this.messages[idx], reply, agents, cybos);
    this.messages = this.messages.slice();
  }

  // Replace the whole list, always chronologically sorted. Fetches must go
  // through this (not a raw assignment) so a server result ordered by a stale
  // seq can't render out of order on reload.
  //
  // Defensive channel guard (prod incident: a nav-away-and-back / cold reload
  // rendered a DIFFERENT channel's entire history in the message pane while the
  // header/member-count/composer correctly showed the real channel — no console
  // error; root cause was an unguarded stale fetch in selectWorkspace's
  // default-channel bootstrap, see app.svelte.ts). Every full-replace of the
  // active channel's messages goes through this one method, so filtering by
  // `this.activeId` here makes the bug class impossible regardless of which
  // caller forgets to guard: a batch whose OWN channelId doesn't match the
  // channel currently on screen is silently dropped, never rendered. A batch
  // that ends up fully foreign renders empty rather than falling back to
  // another channel's data — self-heals on the next correctly-scoped fetch.
  setMessages(msgs: Message[]): void {
    const onScreen = msgs.filter((m) => m.channelId === this.activeId);
    this.trackSeq(onScreen);
    this.messages = onScreen.sort(compareMessages);
    this.fetchedTailId = this.messages.at(-1)?.id;
  }

  // The newest row that came from a PAGE (setMessages / appendMessages), never a
  // live broadcast. loadNewerMessages cursors off it: while the tail is unloaded a
  // live row sorts to the end of the window, and a page asked for "after" that row
  // would come back empty and close the gap between the two for good.
  fetchedTailId: string | undefined;

  get lastSeq(): number {
    // The monotonic high-water (never regresses on a trimmed window); also scan
    // the loaded messages so a freshly-restored state is still covered.
    let max = this.maxAppliedSeq;
    for (const m of this.messages) {
      if (!m.id.startsWith("local-") && m.seq > max) max = m.seq;
    }
    return max;
  }

  prependMessages(msgs: Message[], hasMore: boolean): void {
    this.trackSeq(msgs);
    const existingIds = new Set(this.messages.map((m) => m.id));
    // Same defensive channel guard as setMessages — an older-history page must
    // belong to the channel on screen too, never spliced in from elsewhere.
    const newMsgs = msgs.filter((m) => !existingIds.has(m.id) && m.channelId === this.activeId);
    this.messages = [...newMsgs, ...this.messages].sort(compareMessages);
    this.hasMore = hasMore;
  }

  // b1 load-newer (CYBORG-768): the twin of prependMessages for the NEWER edge.
  // An around-window open (onOpenAtUnread) loads only the unread head, so the
  // true tail stays unloaded (hasMoreNewer); scrolling down near the loaded
  // bottom pages forward with this. Appends (dedup by id + the same defensive
  // channel guard prependMessages uses), sorts ascending, and sets `hasMoreNewer`
  // — NOT `hasMore` (that edge is the OLDER one, untouched here).
  appendMessages(msgs: Message[], hasMoreNewer: boolean): void {
    this.trackSeq(msgs);
    const existingIds = new Set(this.messages.map((m) => m.id));
    const newMsgs = msgs.filter((m) => !existingIds.has(m.id) && m.channelId === this.activeId);
    this.messages = [...this.messages, ...newMsgs].sort(compareMessages);
    this.hasMoreNewer = hasMoreNewer;
    const pageTail = pageTailId(msgs.filter((m) => m.channelId === this.activeId));
    if (pageTail) this.fetchedTailId = pageTail;
  }

  clear(): void {
    this.messages = [];
    this.fetchedTailId = undefined;
    // Reset the sync high-water too: clear() runs on workspace/channel teardown,
    // and a seq from the previous scope must not leak into the next one's drain.
    this.maxAppliedSeq = 0;
    this.hasMore = false;
    this.hasMoreNewer = false;
    this.loading = false;
    this.unreadCursor = null;
    this.clearTyping();
  }

  // Drop the whole typing line + its expiry timers. Called on every channel
  // switch (the activeId setter above) and on teardown (clear).
  //
  // `typing` is keyed by fromId ONLY — an entry carries no channel — so an entry
  // added while channel A was open stayed on screen after switching to channel B
  // and rendered there until its own 5s TTL ran out. For a cybo that is nearly
  // always the full 5s: the server re-emits cyborg:typing_broadcast every 4s for
  // the whole turn (turn-typing-pulse.ts), so the entry is at most ~4s
  // old at the moment you leave, and it re-arms every time you look back in at
  // the busy channel. Mirrors DmState.clearTyping (selectDm) and openThread's
  // `threadState.clearTyping()`, which already did this — the channel surface was
  // the only one of the three that never reset on switch.
  clearTyping(): void {
    this.#typing.clear();
  }

  addTyping(event: TypingEvent): void {
    this.#typing.add(event);
  }

  endTyping(event: TypingEvent): void {
    this.#typing.end(event);
  }

  endTypingAt(fromId: string, parentId: string): boolean {
    return this.#typing.endAt(fromId, parentId);
  }

  // The sender's message arrived: drop their line now instead of after 5 s (#515).
  clearTypingFor(fromId: string): void {
    this.#typing.clearFor(fromId);
  }
}

// CYBORG-1050: a drop must outlast this before the banner appears, so a tab
// refocus (onForeground → forceReconnect) that heals inside it renders nothing.
export const RECONNECT_GRACE_MS = 4_000;

export class ConnectionState {
  private _status: "disconnected" | "connected" | "reconnecting" = $state("disconnected");
  // True once the CURRENT outage episode has outlasted RECONNECT_GRACE_MS.
  private _graceElapsed: boolean = $state(false);
  private graceTimer: ReturnType<typeof setTimeout> | null = null;

  // `status` is NEVER delayed — presence dots and send-guards read it live. Only
  // the banner's own gate (`bannerVisible`) waits out the grace.
  get status(): "disconnected" | "connected" | "reconnecting" {
    return this._status;
  }

  set status(next: "disconnected" | "connected" | "reconnecting") {
    this._status = next;
    pauseTypingWhileOffline(next);
    // Open fires before cyborg:auth: no status, "connected" included, means authenticated.
    this.authenticated = false;
    if (next === "connected") {
      // Connected hides the banner immediately and unconditionally.
      if (this.graceTimer) clearTimeout(this.graceTimer);
      this.graceTimer = null;
      this._graceElapsed = false;
      return;
    }
    // Already inside this outage (timer pending, or grace already spent) — keep
    // the ORIGINAL deadline, so a backoff loop's repeated "reconnecting" emits
    // can't push it out forever and suppress the banner.
    if (this.graceTimer || this._graceElapsed) return;
    this.graceTimer = setTimeout(() => {
      this.graceTimer = null;
      this._graceElapsed = true;
    }, RECONNECT_GRACE_MS);
  }

  // The ConnectionStatus banner's only gate. `deploying` needs no branch of its
  // own: server_shutdown sets status="reconnecting", so it waits out the same grace.
  get bannerVisible(): boolean {
    return this._status !== "connected" && this.hasConnectedOnce && this._graceElapsed;
  }

  // The socket passed cyborg:auth (client "authenticated"); false on any drop. "connected" alone
  // is only OPEN: the relay silently drops a send before auth.
  authenticated: boolean = $state(false);
  // Reactive "a send reaches the relay": effects gating a write on it re-run when it flips.
  get ready(): boolean {
    return this._status === "connected" && this.authenticated;
  }

  error: string | null = $state(null);
  // True once we've connected at least once this session. Lets the
  // ConnectionStatus banner stay hidden during the initial cold-start restore
  // (covered by the splash) and only show for genuine drops afterward.
  hasConnectedOnce: boolean = $state(false);
  // Set when the relay announced a planned restart (cyborg:server_shutdown).
  // Lets the UI show a friendly "Updating…" banner instead of a scary
  // "Disconnected", and reconnect quietly. Cleared on the next successful
  // connection.
  deploying: boolean = $state(false);
  // Reconnect telemetry for the ConnectionStatus banner: the 1-based attempt
  // number and the backoff delay (ms) until the next try. Set on each
  // "reconnecting" emit; reset to 0 on a successful connection.
  reconnectAttempt: number = $state(0);
  reconnectDelayMs: number = $state(0);
}

// ─── Daemon health (polls HTTP /api/health) ──────────────────────

export interface DaemonHealthData {
  relayConnected: boolean;
  pgConnected: boolean;
  uptime: number;
  connectedClients: number;
}

export class DaemonHealthState {
  relayConnected: boolean = $state(false);
  pgConnected: boolean = $state(false);
  uptime: number = $state(0);
  connectedClients: number = $state(0);
  lastChecked: number = $state(0);
  polling: boolean = $state(false);

  private intervalId: ReturnType<typeof setInterval> | null = null;
  private baseUrl: string | null = null;

  get overall(): "connected" | "partial" | "disconnected" {
    if (this.relayConnected && this.pgConnected) return "connected";
    if (this.relayConnected || this.pgConnected) return "partial";
    return "disconnected";
  }

  start(wsUrl: string, intervalMs = 15_000): void {
    this.stop();
    this.baseUrl = wsUrl
      .replace(/^ws:/, "http:")
      .replace(/^wss:/, "https:")
      .replace(/\/ws\/?$/, "");
    this.polling = true;
    this.poll();
    this.intervalId = setInterval(() => this.poll(), intervalMs);
  }

  stop(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    this.polling = false;
    this.relayConnected = false;
    this.pgConnected = false;
    this.uptime = 0;
    this.connectedClients = 0;
  }

  private async poll(): Promise<void> {
    if (!this.baseUrl) return;
    try {
      const resp = await fetch(`${this.baseUrl}/api/health`, { signal: timeoutSignal(5000) });
      if (!resp.ok) return;
      const data = await resp.json();
      this.relayConnected = data.relayConnected ?? false;
      this.pgConnected = data.pgConnected ?? false;
      this.uptime = data.uptime ?? 0;
      this.connectedClients = data.connectedClients ?? 0;
      this.lastChecked = Date.now();
    } catch {
      // fetch failed — daemon unreachable, keep last known state
    }
  }
}

// ─── Presence state (online members via daemon) ──────────────────

export class PresenceState {
  private _onlineIds: Set<string> = $state(new Set());
  // P2 Item 6: users who manually set themselves away (a subset of online).
  private _awayIds: Set<string> = $state(new Set());

  get onlineIds(): ReadonlySet<string> {
    return this._onlineIds;
  }

  get onlineCount(): number {
    return this._onlineIds.size;
  }

  isOnline(userId: string): boolean {
    return this._onlineIds.has(userId);
  }

  isAway(userId: string): boolean {
    return this._awayIds.has(userId);
  }

  update(userIds: string[]): void {
    this._onlineIds = new Set(userIds);
  }

  updateAway(userIds: string[]): void {
    this._awayIds = new Set(userIds);
  }

  addUser(userId: string): void {
    if (this._onlineIds.has(userId)) return;
    const next = new Set(this._onlineIds);
    next.add(userId);
    this._onlineIds = next;
  }

  removeUser(userId: string): void {
    if (!this._onlineIds.has(userId)) return;
    const next = new Set(this._onlineIds);
    next.delete(userId);
    this._onlineIds = next;
  }

  clear(): void {
    this._onlineIds = new Set();
    this._awayIds = new Set();
  }
}

// Other workspace members' custom statuses (P2 Item 6), keyed by userId. Synced
// from the relay (fetch on open/reconnect + live user_status_changed broadcasts)
// so the emoji shows where presence shows. Self-status lives in UserStatusState.
// #671: the snapshot/live-change reconcile logic lives in status-reconcile.ts
// as pure functions (unit-tested there); this class just holds the rune-backed
// map and assigns what they return.
export type { MemberStatus };

export class WorkspaceUserStatusesState {
  private _byUser: Readonly<Record<string, MemberStatus>> = $state({});
  private pruneTimer: ReturnType<typeof setTimeout> | undefined;

  // An expired entry never renders, even before the prune timer drops it.
  get(userId: string): MemberStatus | null {
    const status = this._byUser[userId];
    return status && isLiveStatus(status, Date.now()) ? status : null; // null: get()'s existing "no status" contract
  }

  emojiFor(userId: string): string | null {
    return this.get(userId)?.emoji ?? null; // null: emojiFor's existing string | null contract
  }

  // Replace the whole map from a fetch (snapshot semantics, like presence).
  seed(statuses: Array<{ userId: string } & MemberStatus>): void {
    this.assign(reconcileStatusSnapshot(statuses));
  }

  set(userId: string, status: MemberStatus): void {
    this.assign(applyStatusChange(this._byUser, userId, status));
  }

  clear(): void {
    this.assign({});
  }

  private assign(next: Readonly<Record<string, MemberStatus>>): void {
    this._byUser = pruneExpiredStatuses(next, Date.now());
    if (this.pruneTimer) clearTimeout(this.pruneTimer);
    this.pruneTimer = undefined;
    const soonest = nextStatusExpiry(this._byUser);
    if (soonest === undefined) return;
    const delay = Math.min(Math.max(0, soonest - Date.now()) + 50, STATUS_PRUNE_MAX_DELAY_MS);
    this.pruneTimer = setTimeout(() => this.assign(this._byUser), delay);
  }
}

const STATUS_PRUNE_MAX_DELAY_MS = 24 * 60 * 60 * 1000; // setTimeout fires at once past 2^31 ms

export class AuthState {
  private _user: CyborgUser | null = $state(null);
  private _sessionRevision = $state(0);

  get user(): CyborgUser | null {
    return this._user;
  }

  set user(user: CyborgUser | null) {
    if (
      this._user?.id !== user?.id ||
      this._user?.email.trim().toLowerCase() !== user?.email.trim().toLowerCase()
    ) {
      this._sessionRevision += 1;
    }
    this._user = user;
  }

  // An email comparison cannot detect logout followed by login to the same account.
  get sessionRevision(): number {
    return this._sessionRevision;
  }
  token: string | null = $state(null);
  profileImage: string | null = $state(null);
  private _memberImages: Record<string, string> = $state({});
  private _memberNames: Record<string, string> = $state({});
  // External Slack guests (`slack:<team>:<user>`) and mirrored bots (`slack-bot:…`) learned
  // from a LIVE message rather than from the roster (CYBORG-956). Their own maps, not the member ones, for two reasons:
  // setMemberImagesFromMembers RECONCILES (it deletes every key absent from the payload),
  // and the members_updated broadcast carries members ONLY — so a guest parked in the
  // member maps is wiped the moment anybody joins or changes role. These are display-only
  // identities that cost no seat, so they are additive and never reconciled away.
  private _externalImages: Record<string, string> = $state({});
  private _externalNames: Record<string, string> = $state({});
  // MY OWN per-workspace photo override for the ACTIVE workspace (0121), read
  // off my row in the roster. Every OTHER member's override already arrives
  // pre-resolved in `name`/`imageUrl` (the server COALESCEs it), so only self
  // needs its own slot: getMemberImage/getMemberName short-circuit on self to the
  // live `user` object + the optimistic `profileImage`, which are GLOBAL.
  // null here = "no override in this workspace" ⇒ the global value shows, and the
  // optimistic global-avatar preview keeps working untouched.
  private _selfWorkspaceImage: string | null = $state(null);

  // Avatars are PG-backed now (users.image_url). Reset the in-memory caches;
  // they get repopulated from the auth response (self) and the members fetch.
  bindUser(_userId: string): void {
    this.profileImage = null;
    this._memberImages = {};
    this._memberNames = {};
    this._externalImages = {};
    this._externalNames = {};
    this._selfWorkspaceImage = null;
  }

  clearLocal(): void {
    this.user = null;
    this.token = null;
    this.profileImage = null;
    this._memberImages = {};
    this._memberNames = {};
    this._externalImages = {};
    this._externalNames = {};
    this._selfWorkspaceImage = null;
  }

  get authenticated(): boolean {
    return this.user !== null && this.token !== null;
  }

  // Profile images now live in PG (users.image_url), served via auth + members.
  // setProfileImage updates the in-session copy; persistence + S3 upload happen
  // in the caller (ProfileMenu -> uploadAsset + client.setProfileImage).
  setProfileImage(url: string | null): void {
    this.profileImage = url;
    if (this.user) this.user = { ...this.user, imageUrl: url };
  }

  // Updates the in-session display name. NOTE: there is currently no relay RPC
  // to persist the user's name (only cyborg:set_profile_image exists), so this
  // is an in-memory update for the active session only.
  setProfileName(name: string): void {
    if (this.user) this.user = { ...this.user, name };
  }

  // My per-workspace override for the ACTIVE workspace, or null when I'm using my
  // global profile here. Exposed so the settings panel can show the override vs
  // the inherited global, and so setWorkspaceProfile can patch it optimistically.
  get selfWorkspaceImage(): string | null {
    return this._selfWorkspaceImage;
  }

  // Optimistic write for MY OWN per-workspace override (0121). null clears it back
  // to the global profile. The relay's members_updated broadcast reconciles it.
  setSelfWorkspaceProfile(patch: { image?: string | null }): void {
    if (patch.image !== undefined) this._selfWorkspaceImage = patch.image;
  }

  // MY OWN identity as everyone in this workspace sees it — the SAME resolution
  // the server COALESCEs for every other member (per-workspace override first,
  // account value as the fallback). Every surface that paints the current user
  // must read these, never `user.imageUrl` / `user.name` directly: reading the
  // account value straight is what made a photo set in ONE workspace show up in
  // the OTHER one (the rail / profile-menu avatar bug). Thin wrappers over
  // getMemberImage/getMemberName so there is exactly ONE resolution rule.
  get selfImage(): string | null {
    return this.user?.id ? this.getMemberImage(this.user.id) : null;
  }
  get selfName(): string | null {
    return this.user?.id ? this.getMemberName(this.user.id) : null;
  }

  getMemberImage(userId: string): string | null {
    // Self: my override for THIS workspace wins, else my global avatar (or the
    // just-uploaded optimistic preview, which is a global change by definition).
    if (isMe(userId, this.user)) {
      return this._selfWorkspaceImage ?? this.user?.imageUrl ?? this.profileImage;
    }
    // Roster first, then the live-message fallback for an external guest the roster
    // snapshot predates (CYBORG-956).
    return this._memberImages[userId] ?? this._externalImages[userId] ?? null;
  }

  // Resolve a member's display name by user id. Used so activity rows whose
  // server-side actor_name is missing show a human name instead of a raw UUID.
  getMemberName(userId: string): string | null {
    // Self: my account name — the one name, no per-workspace override.
    if (isMe(userId, this.user)) return this.user?.name ?? null;
    return this._memberNames[userId] ?? this._externalNames[userId] ?? null;
  }

  // Learn an external Slack guest's identity from a LIVE message (CYBORG-956). The guest's
  // very first message reaches every open client BEFORE the row it was mirrored from is
  // persisted (injectMessage broadcasts, then persists), and cyborg:list_members finds an
  // external participant only through their persisted messages — so a refetch on receipt
  // would race the insert and the profile panel would keep showing "User" + a letter avatar
  // until the next reconnect. The broadcast already carries the resolved identity; this
  // stores it. Ignores anyone the bridge did not mint — a `<provider>:` guest or a
  // `<provider>-bot:` bot (a member's identity is the roster's job, and the roster is
  // authoritative) — and never overwrites with a blank.
  rememberExternalIdentity(userId: string, name?: string | null, imageUrl?: string | null): void {
    if (!isBridgedAuthor(userId)) return;
    if (name) this._externalNames[userId] = name;
    if (imageUrl) this._externalImages[userId] = imageUrl;
  }

  // Populate member avatars + names from the PG-backed members list, so every
  // member sees every other member's avatar (no longer localStorage) and name.
  setMemberImagesFromMembers(
    members: Array<{
      userId: string;
      imageUrl?: string | null;
      name?: string | null;
      // The RAW per-workspace photo override (0121). Present only on a relay roster;
      // a daemon roster omits it, which is why it's read with an `in` guard below
      // rather than trusted as "absent ⇒ no override".
      workspaceAvatarUrl?: string | null;
    }>,
  ): void {
    // My own row's RAW photo override, so getMemberImage can prefer it over my
    // GLOBAL user object. Only reconciled from a roster that actually carries the
    // fields — a daemon roster (which has neither) must not clear a relay-loaded
    // override, the same clobbering trap the avatar block below documents.
    // isMe, not `=== this.user.id`: a DAEMON roster stamps the local id, so a raw
    // compare never finds my own row and my per-workspace avatar (0121) is
    // silently ignored. No user ⇒ isMe is false for every row ⇒ undefined, as before.
    const mine = members.find((m) => isMe(m.userId, this.user));
    if (mine && "workspaceAvatarUrl" in mine) {
      this._selfWorkspaceImage = mine.workspaceAvatarUrl ?? null;
    }

    const nextNames: Record<string, string> = {};
    for (const m of members) {
      if (m.name) nextNames[m.userId] = m.name;
    }
    // Mutate the $state maps in place (diff, not wholesale reassign) so Svelte's
    // deep-reactivity proxy only notifies the keys that actually changed — a
    // members refresh that touches one member won't re-run every avatar/name
    // effect observing the others.
    reconcileStateMap(this._memberNames, nextNames);

    // Avatars: ONLY reconcile from a roster that actually CARRIES avatar data. The
    // relay roster includes `imageUrl` for every member; a daemon roster omits the
    // field entirely (its list_members map has no imageUrl, and teammate avatars
    // aren't synced into the daemon's SQLite). Because reconcileStateMap DELETES
    // keys absent from `next`, feeding it an image-less daemon roster used to WIPE
    // every relay-loaded teammate avatar — turning them all into letter avatars
    // (only your own survived, since it comes from authState, not this map). Skip
    // payloads that carry no avatar field so a daemon refresh can't clobber them.
    const carriesAvatars = members.some((m) => "imageUrl" in m);
    if (carriesAvatars) {
      const nextImages: Record<string, string> = {};
      for (const m of members) {
        if (m.imageUrl) nextImages[m.userId] = m.imageUrl;
      }
      reconcileStateMap(this._memberImages, nextImages);
    }
  }
}

// Reconcile a $state record toward `next` by mutating it in place: drop keys no
// longer present, set only the keys whose value changed. Leaves unchanged keys
// untouched so their reactive subscribers don't re-run.
function reconcileStateMap(target: Record<string, string>, next: Record<string, string>): void {
  for (const key of Object.keys(target)) {
    if (!(key in next)) delete target[key];
  }
  for (const [key, value] of Object.entries(next)) {
    if (target[key] !== value) target[key] = value;
  }
}

export interface UserStatus {
  emoji: string | null;
  text: string | null;
  expiresAt: string | null;
}

const STATUS_BASE = "cyborg7_user_status";

export class UserStatusState {
  emoji: string | null = $state(null);
  text: string | null = $state(null);
  expiresAt: string | null = $state(null);

  private clearTimer: ReturnType<typeof setTimeout> | null = null;
  private _boundUserId: string | null = null;

  // null while unbound. It used to fall back to the UNSUFFIXED base key, so a
  // persist() after clearLocal() (logout unbinds but leaves this store live)
  // wrote the departing account's data into a shared global bucket. Nothing ever
  // read it back — every read here sits inside bindUser, which binds first — so
  // it was residue rather than a live cross-user read, but a write no one can
  // ever read is worth nothing and shouldn't be on disk. Same change in
  // NotificationState / UnreadFlagState / DmActivityState / ActivityState /
  // FavoritesState / OpenedDmsState / NotifyClientPrefsState.
  private get statusKey(): string | null {
    return this._boundUserId ? `${STATUS_BASE}_${this._boundUserId}` : null;
  }

  bindUser(userId: string): void {
    this._boundUserId = userId;
    this.emoji = null;
    this.text = null;
    this.expiresAt = null;
    this.load();
    this.scheduleAutoClear();
  }

  clearLocal(): void {
    this._boundUserId = null;
    this.emoji = null;
    this.text = null;
    this.expiresAt = null;
    if (this.clearTimer) {
      clearTimeout(this.clearTimer);
      this.clearTimer = null;
    }
  }

  get active(): boolean {
    return this.emoji !== null || this.text !== null;
  }

  get tooltip(): string {
    if (!this.active) return "";
    const parts: string[] = [];
    if (this.emoji) parts.push(this.emoji);
    if (this.text) parts.push(this.text);
    if (this.expiresAt) {
      const remaining = new Date(this.expiresAt).getTime() - Date.now();
      if (remaining > 0) {
        const mins = remaining / 60_000;
        if (mins < 60) {
          parts.push(`· ${Math.ceil(mins)} min`);
        } else {
          const d = new Date(this.expiresAt);
          parts.push(`· until ${d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`);
        }
      }
    }
    return parts.join(" ");
  }

  setStatus(emoji: string | null, text: string | null, expiresAt: string | null): void {
    this.emoji = emoji;
    this.text = text;
    this.expiresAt = expiresAt;
    this.persist();
    this.scheduleAutoClear();
  }

  // The server row overwrites this device's cache; absent = no status.
  applyServer(status: MemberStatus | undefined): void {
    if (!status || !isLiveStatus(status, Date.now())) {
      this.clearStatus();
      return;
    }
    this.setStatus(status.emoji, status.text, epochMsToIso(status.expiresAt));
  }

  clearStatus(): void {
    this.emoji = null;
    this.text = null;
    this.expiresAt = null;
    this.persist();
    if (this.clearTimer) {
      clearTimeout(this.clearTimer);
      this.clearTimer = null;
    }
  }

  private persist(): void {
    const key = this.statusKey;
    if (!key) return; // unbound (signed out) ⇒ no owner to write under.
    try {
      if (!this.active) {
        localStorage.removeItem(key);
      } else {
        localStorage.setItem(
          key,
          JSON.stringify({ emoji: this.emoji, text: this.text, expiresAt: this.expiresAt }),
        );
      }
    } catch {
      // intentional: best-effort persistence of user status; not user-facing.
    }
  }

  private load(): void {
    const key = this.statusKey;
    if (!key) return;
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return;
      const parsed: UserStatus = JSON.parse(raw);
      if (parsed.expiresAt && new Date(parsed.expiresAt).getTime() <= Date.now()) {
        localStorage.removeItem(key);
        return;
      }
      this.emoji = parsed.emoji;
      this.text = parsed.text;
      this.expiresAt = parsed.expiresAt;
    } catch {
      // intentional: best-effort hydrate of user status; falls back to unset.
    }
  }

  private scheduleAutoClear(): void {
    if (this.clearTimer) {
      clearTimeout(this.clearTimer);
      this.clearTimer = null;
    }
    if (!this.expiresAt) return;
    const remaining = new Date(this.expiresAt).getTime() - Date.now();
    if (remaining <= 0) {
      this.clearStatus();
      return;
    }
    if (remaining > STATUS_PRUNE_MAX_DELAY_MS) {
      this.clearTimer = setTimeout(() => this.scheduleAutoClear(), STATUS_PRUNE_MAX_DELAY_MS);
      return;
    }
    this.clearTimer = setTimeout(() => {
      this.clearStatus();
    }, remaining);
  }
}

// The open agent DM's frozen "New" cursor (CYBORG-1556): DmState's divider rules (frozen
// at open, forward only, null = no divider) keyed by the canonical agent DM peer id.
export class AgentDmReadState {
  peerId = $state<string | null>(null);
  unreadCursor = $state<number | null>(null);
  following = $state(true);

  open(peerId: string, ms: number | null): void {
    this.peerId = peerId;
    this.unreadCursor = ms;
    this.following = true;
  }

  close(): void {
    this.following = true;
    this.peerId = null;
    this.unreadCursor = null;
  }

  canRead(peerId: string): boolean {
    return this.peerId !== peerId || this.following;
  }

  setFollowing(peerId: string, following: boolean): void {
    if (peerId === this.peerId) this.following = following;
  }

  cursorFor(peerId: string | null | undefined): number | null {
    return peerId && peerId === this.peerId ? this.unreadCursor : null; // null: no divider
  }

  // Mirrors DmState.advanceUnreadCursor: forward only, null stays null, open peer only.
  advanceUnreadCursor(peerId: string, ms: number): void {
    if (peerId !== this.peerId) return;
    if (this.unreadCursor !== null && ms > this.unreadCursor) this.unreadCursor = ms;
  }
}

export class DmState {
  activePeerId: string | null = $state(null);
  messages: Message[] = $state([]);
  hasMore = $state(false);
  // CYBORG-767: newer-than-window DM messages exist (around-window open). Mirrors
  // ChannelState.hasMoreNewer.
  hasMoreNewer = $state(false);
  // The relay says this peer is an erased person (additive field of the history read; absent from an older relay).
  peerErased = $state(false);
  // The peer whose first history read has answered (or been given up on): until then an unknown id may be an erased person.
  readPeerId: string | null = $state(null);
  loading = $state(false);
  // In-conversation typing line for the OPEN DM: the same TypingLines ChannelState uses.
  readonly #typing = new TypingLines();
  get typing(): TypingEvent[] {
    return this.#typing.typing;
  }

  // ─── Item 12: unread divider (Slack-mirror), mirrors ChannelState. ───
  // Frozen `lastReadAt` (epoch ms) captured at DM-open from readCursorState,
  // BEFORE the badge clears. null = no cursor (no divider).
  unreadCursor = $state<number | null>(null);

  setUnreadCursor(ms: number | null): void {
    this.unreadCursor = ms;
  }

  // Mirrors ChannelState.advanceUnreadCursor: forward only, null stays null.
  advanceUnreadCursor(ms: number): void {
    if (this.unreadCursor !== null && ms > this.unreadCursor) this.unreadCursor = ms;
  }

  addTyping(event: TypingEvent): void {
    this.#typing.add(event);
  }

  // The sender's DM arrived: drop their line now instead of after 5 s (#515).
  clearTypingFor(fromId: string): void {
    this.#typing.clearFor(fromId);
  }

  // Drop the in-conversation typing line + its timers when switching DMs.
  clearTyping(): void {
    this.#typing.clear();
  }

  addMessage(msg: Message): void {
    if (this.messages.find((m) => m.id === msg.id)) return;
    if (isSettledDuplicate(this.messages, msg)) return;
    const localIdx = findOptimisticIndex(this.messages, msg);
    if (localIdx >= 0) {
      // Reconciled: the server echo replaces the optimistic local row. Mirror the
      // channel path — a transient "sent" check that auto-clears. No clientMsgId
      // fixup needed: CYBORG-801 bars an untagged echo from settling a TAGGED row,
      // so messageKey survives the local→server id swap either way (#msg-remount).
      const settledId = msg.id;
      this.messages = replaceSorted(this.messages, localIdx, { ...msg, sendStatus: "sent" });
      setTimeout(() => this.clearSentMarker(settledId), 1800);
    } else {
      this.messages = insertMessageSorted(this.messages, msg);
    }
  }

  // Drop the transient "sent" check once the reconciled row has settled.
  private clearSentMarker(id: string): void {
    const idx = this.messages.findIndex((m) => m.id === id);
    if (idx < 0 || this.messages[idx].sendStatus !== "sent") return;
    const { sendStatus: _drop, ...rest } = this.messages[idx];
    this.messages[idx] = rest;
    this.messages = this.messages.slice();
  }

  // Update the optimistic send-status of a still-local message (pending → failed,
  // or back to pending on retry). No-op once the row has reconciled to a server id.
  setSendStatus(id: string, status: "pending" | "sent" | "failed", reason?: string): void {
    const idx = this.messages.findIndex((m) => m.id === id);
    if (idx < 0) return;
    this.messages = withSendStatus(this.messages, idx, status, reason);
  }

  removeMessage(id: string): void {
    this.messages = this.messages.filter((m) => m.id !== id);
  }

  // The daemon turned a command away AFTER acking it (queue full or wait bound hit): the row the
  // person sees as sent never ran, so it is marked failed, found by the client id (it may already
  // carry the server's id). Returns the row's text, or undefined when no such row is loaded.
  failSentByClientMsgId(clientMsgId: string, reason?: string): string | undefined {
    const idx = this.messages.findIndex((m) => m.clientMsgId === clientMsgId);
    if (idx < 0) return undefined;
    const row = { ...this.messages[idx], sendStatus: "failed" as const, sendFailedReason: reason };
    this.messages = replaceSorted(this.messages, idx, row);
    return row.text;
  }

  // Bump a DM root's thread indicator when a reply arrives — mirrors
  // ChannelState.bumpReplyCount so DM thread footers update live.
  private countedReplies = new Set<string>(); // same reply id counts once (frame can arrive twice)

  bumpReplyCount(
    parentId: string,
    reply: LiveReply,
    agents?: ReplyAgents,
    cybos?: ReplyCybos,
  ): void {
    if (!countsAsThreadReply(reply)) return;
    if (this.countedReplies.has(reply.id)) return;
    this.countedReplies.add(reply.id);
    if (this.countedReplies.size > 2000) {
      this.countedReplies.delete(this.countedReplies.values().next().value as string);
    }
    const idx = this.messages.findIndex((m) => m.id === parentId);
    if (idx < 0) return;
    this.messages[idx] = rootWithReply(this.messages[idx], reply, agents, cybos);
    this.messages = this.messages.slice();
  }

  // Defensive peer guard (mirrors ChannelState.setMessages/prependMessages) — a
  // server batch only merges in if it's actually a DM (no channelId) with the
  // peer currently on screen. Closes the same bug CLASS for DMs, e.g.
  // loadMoreDmMessages doesn't re-check activePeerId after its await, so a fast
  // peer switch mid-pagination could otherwise splice the OLD peer's older
  // history into the NEW peer's conversation.
  // `requestPeerId`: the peer the READ was made for. The relay masks an erased person's ids on the wire, so
  // such a row cannot be matched to the pane by fromId/toId; the batch belongs to the pane that requested it.
  mergeServer(serverMessages: Message[], requestPeerId?: string): Message[] {
    const onScreen = serverMessages.filter((m) =>
      requestPeerId !== undefined
        ? !m.channelId && requestPeerId === this.activePeerId
        : !m.channelId && (m.fromId === this.activePeerId || m.toId === this.activePeerId),
    );
    const merged = new Map<string, Message>();
    for (const m of this.messages) merged.set(m.id, m);
    for (const m of onScreen) {
      // Collapse the optimistic local- row this server message settles (#501):
      // clientMsgId-first, falling back to (fromId,text). Mirrors addMessage's
      // findOptimisticIndex but over the merge map's values.
      const mergedValues = [...merged.values()];
      const localIdx = findOptimisticIndex(mergedValues, m);
      if (localIdx >= 0) merged.delete(mergedValues[localIdx].id);
      merged.set(m.id, m);
    }
    this.messages = [...merged.values()].sort(compareMessages);
    return onScreen;
  }

  // Twin of ChannelState.fetchedTailId: the newest row a PAGE returned, never a live row
  // or a `local-` bubble, so loadNewerDmMessages never pages "after" a non-page position.
  fetchedTailId: string | undefined;

  // Merge a page that reaches the window's newer edge (open, around, newer, latest).
  mergePage(page: Message[], requestPeerId?: string): void {
    const tail = pageTailId(this.mergeServer(page, requestPeerId));
    if (tail) this.fetchedTailId = tail;
  }

  // Highest server seq among loaded DM messages — the cursor for the seq-drained
  // reconnect catch-up (#500). Ignores optimistic local- rows (seq 0). 0 when the
  // conversation hasn't loaded any server message.
  //
  // Deliberately NOT a mirror of ChannelState.lastSeq: there is no monotonic
  // `maxAppliedSeq` high-water here, and adding one would be a bug, not
  // symmetry. ChannelState needs it because a regressed channel cursor re-asks
  // `cyborg:sync` from a low seq and permanently SKIPS messages (#844) — the
  // page itself can no longer clobber the window (applyChannelSyncPage unions,
  // it never replaces), but the cursor must still only rise. The DM drain has
  // none of that: `catchUpDm` reads this once to seed a cursor, `cyborg:fetch_dm_since` only ever returns
  // `seq > sinceSeq` ascending, and every page lands in `mergeServer` (a
  // union by id, never a replace). Nothing acks or persists this value, so it
  // can only err LOW — costing a redundant refetch, never a skip.
  //
  // A high-water would actively HURT: `selectDm` resets a peer switch by
  // assigning `dmState.messages = []` directly, bypassing `clear()`, so peer A's
  // high-water would survive into peer B's drain and make the cursor too HIGH —
  // permanently skipping B's messages, which is the very failure #844 describes.
  get lastSeq(): number {
    let max = 0;
    for (const m of this.messages) {
      if (!m.id.startsWith("local-") && m.seq > max) max = m.seq;
    }
    return max;
  }

  clear(): void {
    this.messages = [];
    this.fetchedTailId = undefined;
    this.hasMore = false;
    this.hasMoreNewer = false;
    this.loading = false;
    this.#typing.clear();
    this.unreadCursor = null;
    this.readPeerId = null;
  }
}

// Workspace-scoped DM typing, keyed by the typing peer's fromId. Lets the
// SIDEBAR show a typing badge on a DM row even when that DM is NOT the active
// view. Each entry self-expires after 5s (same as the in-conversation line).
export class DmTypingState {
  readonly #typing = new TypingLines();

  isTyping(peerId: string): boolean {
    return this.#typing.typing.some((t) => t.fromId === peerId);
  }

  // The dot needs only the sender; the other fields have no meaning here.
  mark(peerId: string): void {
    this.#typing.add({ workspaceId: "", channelId: "", fromId: peerId });
  }

  // The peer's DM arrived: drop the dot now instead of after 5 s (#515).
  clearFor(peerId: string): void {
    this.#typing.clearFor(peerId);
  }

  clear(): void {
    this.#typing.clear();
  }
}

export const dmTypingState = new DmTypingState();

// ─── Notification state (single source of truth for unread badges) ─

const NOTIFICATION_COUNTS_BASE = "cyborg7_notification_counts";

export class NotificationState {
  private _counts: Record<string, Record<string, number>> = $state({});
  private _boundUserId: string | null = null;
  // Wall-clock time of the last LOCAL increment() per (workspaceId, targetId).
  // Bookkeeping only — never rendered, so plain (non-$state) is correct and
  // avoids extra reactivity churn. Lets seedCounts (below) tell a live increment
  // that raced ahead of an authoritative snapshot from one the snapshot already
  // knows is stale, instead of wiping every live badge on every reseed.
  private _incrementedAt: Record<string, Record<string, number>> = {};
  // Wall-clock time of the last RETIREMENT per (workspaceId, targetId) — see
  // `retire`. The mirror image of `_incrementedAt`: that map stops a reseed from
  // wiping a badge the snapshot is too OLD to know about; this one stops the same
  // too-old snapshot from RESURRECTING a badge whose surface has since gone away.
  // Bookkeeping only, never rendered, never persisted (a retirement deletes the
  // count, and the deletion is what survives a reload).
  private _retiredAt: Record<string, Record<string, number>> = {};

  // null while unbound — see UserStatusState.statusKey.
  private get countsKey(): string | null {
    return this._boundUserId ? `${NOTIFICATION_COUNTS_BASE}_${this._boundUserId}` : null;
  }

  bindUser(userId: string): void {
    this._boundUserId = userId;
    this._counts = {};
    this._incrementedAt = {};
    this._retiredAt = {};
    const key = this.countsKey;
    if (!key) return;
    try {
      const raw = localStorage.getItem(key);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === "object") this._counts = parsed;
      }
    } catch {
      // intentional: best-effort hydrate of notification counts; falls back to empty.
    }
  }

  clearLocal(): void {
    this._boundUserId = null;
    this._counts = {};
    this._incrementedAt = {};
    this._retiredAt = {};
  }

  increment(workspaceId: string, targetId: string): void {
    if (!this._counts[workspaceId]) this._counts[workspaceId] = {};
    this._counts[workspaceId][targetId] = (this._counts[workspaceId][targetId] ?? 0) + 1;
    this._counts = { ...this._counts };
    if (!this._incrementedAt[workspaceId]) this._incrementedAt[workspaceId] = {};
    this._incrementedAt[workspaceId][targetId] = Date.now();
    this.persist();
  }

  clear(workspaceId: string, targetId: string): void {
    delete this._incrementedAt[workspaceId]?.[targetId];
    if (!this._counts[workspaceId]?.[targetId]) return;
    const ws = { ...this._counts[workspaceId] };
    delete ws[targetId];
    this._counts = { ...this._counts, [workspaceId]: ws };
    this.persist();
  }

  // Drop a badge whose SURFACE IS GONE — the other half of the door invariant
  // (#3148). `clear` means "read"; this means "there is nothing left to read it
  // in", and the two need different reseed semantics. getDmUnreadCounts only
  // counts a peer that is still in `live_senders` (a workspace member, or an
  // agent_sessions row with `archived_at IS NULL`) or still a row in `cybos`, so
  // once the last of those goes the server can never name that peer again: no
  // reseed will ever return the key, and no DM row renders for it either. The
  // count is left holding a number nothing can confirm and nothing can click.
  //
  // `retiredAt` is why this is not just `clear`. The reseed that would otherwise
  // undo it can be IN FLIGHT: its snapshot was taken while the surface still
  // existed, so it still reports the peer and would put the badge straight back.
  // seedCounts therefore ignores any key retired AT OR AFTER the snapshot's
  // `seededAt` (>= for the same millisecond-resolution reason as the increment
  // guard). A snapshot taken AFTER the retirement that still reports the key is
  // the server genuinely disagreeing — the peer came back — and it wins.
  retire(workspaceId: string, targetId: string, at: number = Date.now()): void {
    if (!this._retiredAt[workspaceId]) this._retiredAt[workspaceId] = {};
    this._retiredAt[workspaceId][targetId] = at;
    this.clear(workspaceId, targetId);
  }

  clearWorkspace(workspaceId: string): void {
    delete this._incrementedAt[workspaceId];
    delete this._retiredAt[workspaceId];
    if (!this._counts[workspaceId]) return;
    const next = { ...this._counts };
    delete next[workspaceId];
    this._counts = next;
    this.persist();
  }

  // Replace a workspace's unread counts with server truth (P6: on workspace
  // load, so badges survive reload and sync across devices). Zero counts dropped.
  //
  // RACE GUARD: seedCounts used to be a blind full replace, so an authoritative
  // reseed (fires on every foreground ≥10s, reconnect, and workspace switch —
  // unread-reconcile.ts) could wipe a badge that was incremented LOCALLY, live,
  // while the reseed's own server query was in flight — the query's snapshot
  // simply predates the message. `seededAt` is the caller's clock reading taken
  // BEFORE it issued the fetch_unread request; any local increment stamped AT OR
  // AFTER that moment might not be reflected in `counts` yet (>= — not > — since
  // Date.now() has only millisecond resolution and the two reads can land in the
  // same tick), so it survives the replace instead of being dropped. An
  // increment stamped strictly BEFORE `seededAt` that's missing from `counts`
  // means the server has since confirmed it read (e.g. from another device) —
  // that one is correctly dropped, keeping the reseed authoritative for
  // everything it actually knows about.
  seedCounts(
    workspaceId: string,
    counts: Record<string, number>,
    seededAt: number = Date.now(),
  ): void {
    const filtered: Record<string, number> = {};
    for (const [id, n] of Object.entries(counts)) {
      if (n > 0) filtered[id] = n;
    }
    // RETIREMENT GUARD (the mirror of the race guard below). A key retired AT OR
    // AFTER `seededAt` was retired after this snapshot was taken, so the snapshot
    // cannot know the surface is gone and must not put the badge back. Clamped +
    // pruned on the same rules as `_incrementedAt`: the retirement only has to
    // outlive the snapshots that predate it, so the NEXT reseed (a later
    // `seededAt`) drops the bookkeeping and lets the server speak again — this is
    // a door, not a permanent mute.
    const priorRetiredAt = this._retiredAt[workspaceId] ?? {};
    const survivingRetiredAt: Record<string, number> = {};
    for (const [id, at] of Object.entries(priorRetiredAt)) {
      if (at < seededAt) continue;
      delete filtered[id];
      survivingRetiredAt[id] = Math.min(at, seededAt);
    }
    this._retiredAt = { ...this._retiredAt, [workspaceId]: survivingRetiredAt };
    const priorIncrementedAt = this._incrementedAt[workspaceId] ?? {};
    const priorCounts = this._counts[workspaceId] ?? {};
    const survivingIncrementedAt: Record<string, number> = {};
    for (const [id, at] of Object.entries(priorIncrementedAt)) {
      if (at >= seededAt && !(id in filtered)) {
        const prior = priorCounts[id];
        if (prior) filtered[id] = prior;
      }
      // Keep the timestamp only for ids the snapshot didn't just settle — once an
      // id has a value again (either from the fresh snapshot or preserved above)
      // its bookkeeping is done; prune the rest so this map never grows unbounded.
      // CLAMP to seededAt rather than keeping the original `at`: a clock
      // step-back (NTP correction, VM resync/sleep-resume) can stamp an
      // increment with a future wall-clock reading, which would otherwise
      // satisfy `at >= seededAt` on EVERY later reconcile forever — pinning an
      // already-read badge as "still racing" indefinitely (round 2 finding #2).
      // Clamping means the very next reconcile after the clock normalizes sees
      // a stamp equal to THIS seededAt, which is < that reconcile's (later)
      // seededAt, so a genuinely-confirmed-read id still correctly drops.
      if (id in filtered) survivingIncrementedAt[id] = Math.min(at, seededAt);
    }
    this._incrementedAt = { ...this._incrementedAt, [workspaceId]: survivingIncrementedAt };
    // Mutate the $state proxy's key in place (Svelte 5 deep reactivity) rather
    // than rebinding the whole `_counts` reference — that way only effects
    // reading THIS workspace's counts re-run, not every workspace's.
    this._counts[workspaceId] = filtered;
    this.persist();
  }

  // Bridges the BUG #2 fetch_unread response shape (mentionCounts + dmCounts)
  // into seedCounts, with one extra rule seedCounts itself can't express: the
  // daemon-direct dispatcher path (message-router.ts handleFetchUnread) answers
  // with `{counts, reads}` ONLY — mentionCounts and dmCounts are `undefined`
  // there, not `{}`. `undefined` means "this response shape doesn't report
  // these at all" (leave whatever's already seeded alone); `{}` means "the
  // server just confirmed zero, right now" (clear them, subject to seedCounts'
  // own race guard). A caller that defaulted both to `{}` before calling
  // seedCounts would wrongly wipe every pre-existing DM/mention badge on every
  // daemon-direct reconcile (round 2 finding #3). See seedUnreadSignals in
  // app.svelte.ts for the real caller.
  seedMentionAndDmCounts(
    workspaceId: string,
    mentionCounts: Record<string, number> | undefined,
    dmCounts: Record<string, number> | undefined,
    seededAt: number,
  ): void {
    if (mentionCounts === undefined && dmCounts === undefined) return;
    // Spreading `undefined` is already a safe no-op — no `?? {}` fallback needed.
    this.seedCounts(workspaceId, { ...mentionCounts, ...dmCounts }, seededAt);
  }

  getCount(workspaceId: string, targetId: string): number {
    return this._counts[workspaceId]?.[targetId] ?? 0;
  }

  getWorkspaceTotal(workspaceId: string): number {
    const ws = this._counts[workspaceId];
    if (!ws) return 0;
    let total = 0;
    for (const count of Object.values(ws)) total += count;
    return total;
  }

  get globalTotal(): number {
    let total = 0;
    for (const ws of Object.values(this._counts)) {
      for (const count of Object.values(ws)) total += count;
    }
    return total;
  }

  // Read-only view of a workspace's (canonical id → count) map — empty if none.
  // Exposed so the shared unread selectors (unread-selectors.ts) can sum only
  // the NON-muted entries instead of every caller re-deriving the total.
  countsForWorkspace(workspaceId: string): Record<string, number> {
    return this._counts[workspaceId] ?? {};
  }

  // Exposed ONLY so tests can verify the reseed race-guard bookkeeping is
  // actually pruned (not just numerically failing its own `>= seededAt` check
  // again on stale-but-untouched data) — see
  // notification-state-reseed-race.test.ts round 2 finding #4.
  incrementedAtForTest(workspaceId: string): Readonly<Record<string, number>> {
    return this._incrementedAt[workspaceId] ?? {};
  }

  // Same purpose as incrementedAtForTest, for the retirement guard: proves a
  // retirement is PRUNED once a later snapshot has been seen, so it can never
  // become a permanent mute on a peer that comes back.
  retiredAtForTest(workspaceId: string): Readonly<Record<string, number>> {
    return this._retiredAt[workspaceId] ?? {};
  }

  // Ids of every workspace that currently holds any count (global rollups).
  get workspaceIds(): string[] {
    return Object.keys(this._counts);
  }

  private persist(): void {
    const key = this.countsKey;
    if (!key) return; // unbound (signed out) ⇒ no owner to write under.
    try {
      localStorage.setItem(key, JSON.stringify(this._counts));
    } catch {
      // intentional: best-effort persistence of notification counts; not user-facing.
    }
  }
}

// ─── Unread-flag state (BUG #2: bold-vs-badge split) ───────────────
// Mattermost/v1 gold standard: a channel has TWO independent unread signals.
//   (a) an unread FLAG → BOLD channel name (any non-muted unread message), and
//   (b) a mention COUNT → the RED NUMBER (held by NotificationState above), which
//       only increments on a real mention.
// This store owns signal (a): a per-workspace Set of channel ids that have an
// unread (non-muted) message. Persisted so bold survives reload.
// DMs are NOT tracked here — they always count (their NotificationState number
// already drives both bold and badge), matching Slack/Mattermost DM behaviour.
const UNREAD_FLAGS_BASE = "cyborg7_unread_flags";

export class UnreadFlagState {
  // workspaceId → Set<channelId>. Stored as a $state object holding Sets;
  // mutations reassign the object so Svelte's runes see the change.
  private _flags: Record<string, Set<string>> = $state({});
  private _boundUserId: string | null = null;
  // Wall-clock time of the last LOCAL markUnread() per (workspaceId,
  // channelId). Bookkeeping only — mirrors NotificationState._incrementedAt
  // exactly (see there for the full race-guard + clock-step-back-clamp
  // rationale); `seed` below uses it the same way `seedCounts` uses
  // `_incrementedAt` (round 2 finding #1: `seed` used to be a blind full
  // replace with no such guard at all, so a plain non-mention message's bold
  // flag — actually the MORE common case, since it doesn't also bump the red
  // count, see notifyForIncoming — could vanish within moments of appearing on
  // any racing reseed).
  private _markedAt: Record<string, Record<string, number>> = {};
  // How many unread root messages a flagged channel has (the server's count, plus live
  // arrivals), so the sidebar can show "1" instead of a bare dot. Memory only: after a
  // reload a flagged channel shows 1 until the next reseed brings the real number.
  private _counts: Record<string, Record<string, number>> = $state({});

  // null while unbound — see UserStatusState.statusKey.
  private get flagsKey(): string | null {
    return this._boundUserId ? `${UNREAD_FLAGS_BASE}_${this._boundUserId}` : null;
  }

  bindUser(userId: string): void {
    this._boundUserId = userId;
    this._flags = {};
    this._markedAt = {};
    this._counts = {};
    const key = this.flagsKey;
    if (!key) return;
    try {
      const raw = localStorage.getItem(key);
      const parsed = raw ? JSON.parse(raw) : null;
      if (!parsed || typeof parsed !== "object") return;
      const next: Record<string, Set<string>> = {};
      for (const [ws, ids] of Object.entries(parsed)) {
        if (Array.isArray(ids)) next[ws] = new Set(ids as string[]);
      }
      this._flags = next;
    } catch {
      // intentional: best-effort hydrate of unread flags; falls back to empty.
    }
  }

  clearLocal(): void {
    this._boundUserId = null;
    this._flags = {};
    this._markedAt = {};
    this._counts = {};
  }

  // `bump: false`: flag without counting a message (a remote rewind).
  markUnread(workspaceId: string, targetId: string, bump = true): void {
    if (!this._markedAt[workspaceId]) this._markedAt[workspaceId] = {};
    this._markedAt[workspaceId][targetId] = Date.now();
    const counts = this._counts[workspaceId] ?? {};
    if (bump) this._counts[workspaceId] = { ...counts, [targetId]: (counts[targetId] ?? 0) + 1 };
    const ws = this._flags[workspaceId] ?? new Set<string>();
    if (ws.has(targetId)) return;
    const next = new Set(ws);
    next.add(targetId);
    this._flags = { ...this._flags, [workspaceId]: next };
    this.persist();
  }

  clearUnread(workspaceId: string, targetId: string): void {
    delete this._markedAt[workspaceId]?.[targetId];
    if (this._counts[workspaceId]?.[targetId] !== undefined) {
      const { [targetId]: _cleared, ...rest } = this._counts[workspaceId];
      this._counts[workspaceId] = rest;
    }
    const ws = this._flags[workspaceId];
    if (!ws?.has(targetId)) return;
    const next = new Set(ws);
    next.delete(targetId);
    this._flags = { ...this._flags, [workspaceId]: next };
    this.persist();
  }

  clearWorkspace(workspaceId: string): void {
    delete this._markedAt[workspaceId];
    if (this._counts[workspaceId]) {
      const { [workspaceId]: _cleared, ...rest } = this._counts;
      this._counts = rest;
    }
    if (!this._flags[workspaceId]) return;
    const next = { ...this._flags };
    delete next[workspaceId];
    this._flags = next;
    this.persist();
  }

  isUnread(workspaceId: string, targetId: string): boolean {
    return this._flags[workspaceId]?.has(targetId) ?? false;
  }

  // Unread root messages in a flagged channel; at least 1 while flagged, 0 otherwise.
  unreadCount(workspaceId: string, targetId: string): number {
    if (!this.isUnread(workspaceId, targetId)) return 0;
    return Math.max(1, this._counts[workspaceId]?.[targetId] ?? 0);
  }

  // True when ANY loaded workspace has at least one channel/DM with an unread
  // flag. Reactive (reads `_flags`), so the dock-badge effect re-runs as flags
  // change. Same source as `isUnread` — the dock "dot" must never drift from the
  // sidebar's bold channels / the settings "Channels with unread" row.
  hasAnyUnread(): boolean {
    for (const ids of Object.values(this._flags)) {
      if (ids.size > 0) return true;
    }
    return false;
  }

  // Read-only view of a workspace's bold-flag id set — empty if none. Exposed so
  // the shared unread selectors can union flag-bearing ids with count-bearing
  // ones when computing anyUnread().
  idsForWorkspace(workspaceId: string): ReadonlySet<string> {
    return this._flags[workspaceId] ?? new Set<string>();
  }

  get workspaceIds(): string[] {
    return Object.keys(this._flags);
  }

  // Exposed ONLY so tests can verify the reseed race-guard bookkeeping is
  // actually pruned — mirrors NotificationState.incrementedAtForTest.
  markedAtForTest(workspaceId: string): Readonly<Record<string, number>> {
    return this._markedAt[workspaceId] ?? {};
  }

  // Replace a workspace's unread flags with server truth (workspace open /
  // reconnect), so bold survives reload + syncs across devices.
  //
  // RACE GUARD (round 2 finding #1): mirrors NotificationState.seedCounts
  // exactly — `seed` used to be a blind full replace, so an authoritative
  // reseed (fires on every foreground ≥10s, reconnect, and workspace switch —
  // unread-reconcile.ts) could un-bold a channel that notifyForIncoming just
  // flagged LOCALLY, live, via markUnread() while the reseed's own server
  // query was in flight. This is actually the MORE common path to a wrong
  // badge than the one seedCounts guards: a plain (non-mention) channel
  // message only sets the bold flag — it never bumps the red count (see
  // notifyForIncoming) — so hasBadge()'s only surviving signal for it is this
  // flag. `seededAt` is the caller's clock reading taken BEFORE it issued the
  // fetch_unread request; any local markUnread stamped AT OR AFTER that moment
  // might not be reflected in `ids` yet (>= — not > — for the same
  // millisecond-tie reason as seedCounts), so it survives the replace. A
  // markUnread stamped strictly BEFORE `seededAt` that's missing from `ids`
  // means the server has since confirmed it read — correctly dropped. The
  // surviving stamp is CLAMPED to `seededAt` (not kept as the original `at`)
  // for the same clock-step-back reason as seedCounts (round 2 finding #2).
  // `counts`: the server's unread number per channel; a channel that survives only through
  // the race guard keeps its local number.
  seed(
    workspaceId: string,
    ids: Iterable<string>,
    seededAt: number = Date.now(),
    counts: Record<string, number> = {},
  ): void {
    const filtered = new Set(ids);
    const priorMarkedAt = this._markedAt[workspaceId] ?? {};
    const priorCounts = this._counts[workspaceId] ?? {};
    const nextCounts: Record<string, number> = {};
    for (const id of filtered) if (counts[id] > 0) nextCounts[id] = counts[id];
    const survivingMarkedAt: Record<string, number> = {};
    for (const [id, at] of Object.entries(priorMarkedAt)) {
      if (at >= seededAt && !filtered.has(id)) {
        filtered.add(id);
        if (priorCounts[id]) nextCounts[id] = priorCounts[id];
      }
      // Prune ids the snapshot didn't just settle, same as seedCounts.
      if (filtered.has(id)) survivingMarkedAt[id] = Math.min(at, seededAt);
    }
    this._markedAt = { ...this._markedAt, [workspaceId]: survivingMarkedAt };
    this._counts[workspaceId] = nextCounts;
    // Mutate the $state proxy's key in place (Svelte 5 deep reactivity) rather
    // than rebinding the whole `_flags` reference — same reasoning as
    // NotificationState.seedCounts.
    this._flags[workspaceId] = filtered;
    this.persist();
  }

  private persist(): void {
    const key = this.flagsKey;
    if (!key) return; // unbound (signed out) ⇒ no owner to write under.
    try {
      const serializable: Record<string, string[]> = {};
      for (const [ws, ids] of Object.entries(this._flags)) serializable[ws] = [...ids];
      localStorage.setItem(key, JSON.stringify(serializable));
    } catch {
      // intentional: best-effort persistence of unread flags; not user-facing.
    }
  }
}

// ─── DM-activity state (#17: DM list ordered by most-recent message) ─
// Slack/Mattermost parity: the DM list surfaces the most recently active
// conversation at the TOP (Mattermost sorts the sidebar by `last_post_at` desc).
// The v2 relay does not yet return a per-peer `last_message_at` on workspace
// load (it would need a join through dm_conversations → channel_messages), so —
// mirroring the original v1 web app (Sidebar.tsx `dmHumanActivity`) — we track
// the last-activity timestamp per peer CLIENT-SIDE and bump it whenever a DM is
// sent, received, or opened. Workspace-scoped, keyed by peer id (a human userId
// OR an agentId), persisted to localStorage so a reload keeps the order. Peers
// with no recorded activity sort to the bottom in their existing (stable) order.
const DM_ACTIVITY_BASE = "cyborg7_dm_activity";

export class DmActivityState {
  // workspaceId → (peerId → epoch ms of last DM activity).
  private _activity: Record<string, Record<string, number>> = $state({});
  private _boundUserId: string | null = null;

  // null while unbound — see UserStatusState.statusKey.
  private get activityKey(): string | null {
    return this._boundUserId ? `${DM_ACTIVITY_BASE}_${this._boundUserId}` : null;
  }

  bindUser(userId: string): void {
    this._boundUserId = userId;
    this._activity = {};
    const key = this.activityKey;
    if (!key) return;
    try {
      const raw = localStorage.getItem(key);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === "object") this._activity = parsed;
      }
    } catch {
      // intentional: best-effort hydrate of DM activity order; falls back to empty.
    }
  }

  clearLocal(): void {
    this._boundUserId = null;
    this._activity = {};
  }

  // Record DM activity for a peer at `ms` (defaults to now). Monotonic: never
  // moves a peer's timestamp backward, so a late re-broadcast can't demote a
  // conversation the user has since interacted with.
  bump(workspaceId: string, peerId: string, ms: number = Date.now()): void {
    if (!workspaceId || !peerId) return;
    const ws = this._activity[workspaceId] ?? {};
    if ((ws[peerId] ?? 0) >= ms) return;
    this._activity = { ...this._activity, [workspaceId]: { ...ws, [peerId]: ms } };
    this.persist();
  }

  // Overlay the relay's own record of each conversation's newest message
  // (`dmLastMessageAt`, 90-day window) onto this workspace's map. This is what
  // makes the order correct on a device the user has never used and after a
  // message that landed while the app was closed — localStorage alone knows
  // neither.
  //
  // A MERGE, never a workspace-wide replace: this map also holds CHANNEL ids
  // (every channel message bumps into it for the mobile inbox) and the server
  // reports only DM peers plus group_dm channels inside its window, so a replace
  // would drop every other key.
  //
  // NOT routed through `bump`. bump is monotonic — it early-returns when the
  // stored value is already >= the incoming one — so it can only move a value
  // UP, and the seed's most valuable job is moving one DOWN: installs still
  // carry clock-skewed-high timestamps written by the client-clock send-time
  // bump that the relay's own `createdAt` has since replaced, and a monotonic
  // write can never heal those. For a key the server reports, the server is the
  // truth.
  //
  // Race guard, the same one NotificationState.seedCounts applies: a local value
  // stamped AT OR AFTER `seededAt` (the caller's clock reading taken BEFORE it
  // issued the request that produced `activity`) is a live bump this snapshot
  // predates, so it survives. `>=` not `>` — Date.now() has millisecond
  // resolution and the two reads can land in the same tick. The stored value is
  // itself a timestamp in the same units, so it doubles as its own race marker;
  // no companion "_bumpedAt" map is needed here.
  seedActivity(
    workspaceId: string,
    activity: Record<string, number>,
    seededAt: number = Date.now(),
  ): void {
    if (!workspaceId) return;
    // Spreading `undefined` is already a safe no-op — no `?? {}` fallback needed.
    const next = { ...this._activity[workspaceId] };
    let changed = false;
    for (const [peerId, ms] of Object.entries(activity)) {
      if (!peerId || !Number.isFinite(ms) || ms <= 0) continue;
      const local = next[peerId] ?? 0;
      if (local >= seededAt || local === ms) continue;
      next[peerId] = ms;
      changed = true;
    }
    if (!changed) return; // nothing moved ⇒ no reassign, no write
    this._activity = { ...this._activity, [workspaceId]: next };
    this.persist();
  }

  getActivity(workspaceId: string, peerId: string): number {
    return this._activity[workspaceId]?.[peerId] ?? 0;
  }

  // Read-only snapshot of the peers with recorded activity, per workspace. Used
  // to seed the "opened DMs" set so conversations that predate that feature
  // survive the DM-list filter's rollout.
  snapshot(): Record<string, string[]> {
    const out: Record<string, string[]> = {};
    for (const [ws, peers] of Object.entries(this._activity)) out[ws] = Object.keys(peers);
    return out;
  }

  private persist(): void {
    const key = this.activityKey;
    if (!key) return; // unbound (signed out) ⇒ no owner to write under.
    try {
      localStorage.setItem(key, JSON.stringify(this._activity));
    } catch {
      // intentional: best-effort persistence of DM activity order; not user-facing.
    }
  }
}

// Per-channel notification preferences (server-backed; P8). 'all' |
// 'mentions_only' | 'muted'. Channels default to 'all' — notify on every
// message (WhatsApp/Telegram-style); users dial down to mentions-only or mute.
export class NotificationPrefsState {
  prefs: Record<string, string> = $state({});

  seed(map: Record<string, string>): void {
    this.prefs = { ...map };
  }

  // Additive seed — keep prefs already loaded for OTHER workspaces and overlay
  // this map on top. Scope ids (channel/peer/agent ids) are globally-unique
  // UUIDs, so a flat cross-workspace map never collides. Used to load every
  // workspace's prefs up front (subscribeAndSeedAllWorkspaces) and on each
  // workspace switch, so the notification policy can resolve the REAL pref for a
  // message arriving in a NON-active workspace (a muted/mentions-only channel in
  // another workspace must not banner a plain message). A plain `seed` on switch
  // would wipe the other workspaces' prefs and re-introduce that bug.
  merge(map: Record<string, string>): void {
    for (const [k, v] of Object.entries(map)) this.prefs[k] = v;
  }

  set(scopeId: string, preference: string): void {
    this.prefs[scopeId] = preference;
  }

  get(scopeId: string): string {
    return this.prefs[scopeId] ?? "all";
  }

  // Drop every loaded pref (logout / account switch) so a previous account's
  // cross-workspace prefs never leak into the next session.
  clear(): void {
    this.prefs = {};
  }
}

export const notifPrefsState = new NotificationPrefsState();

// Channels the viewer archived for themselves (sidebar only, synced across their devices).
export class ArchivedChannelsState {
  ids: Record<string, true> = $state({});

  private seededBy: Record<string, string[]> = {};
  // Channels a message resurfaced this session. A later server reseed (its rule only sees structured
  // mentions of me) must not hide them again; an explicit archive (set(.., true)) forgets them.
  private resurfaced = new Set<string>();

  // Scoped per workspace: the dock/switcher totals read OTHER workspaces too.
  seed(channelIds: string[], workspaceId?: string): void {
    if (!workspaceId) {
      this.seededBy = {};
      this.ids = Object.fromEntries(
        channelIds.filter((id) => !this.resurfaced.has(id)).map((id) => [id, true as const]),
      );
      return;
    }
    const next = { ...this.ids };
    for (const id of this.seededBy[workspaceId] ?? []) delete next[id];
    for (const id of channelIds) if (!this.resurfaced.has(id)) next[id] = true;
    this.seededBy[workspaceId] = channelIds;
    this.ids = next;
  }

  has(channelId: string): boolean {
    return this.ids[channelId] === true;
  }

  set(channelId: string, archived: boolean): void {
    if (archived) {
      this.resurfaced.delete(channelId);
      this.ids[channelId] = true;
    } else delete this.ids[channelId];
  }

  // A message brought the channel back (human root, or a mention of me): stays back across reseeds.
  resurface(channelId: string): void {
    this.resurfaced.add(channelId);
    delete this.ids[channelId];
  }

  clear(): void {
    this.seededBy = {};
    this.resurfaced.clear();
    this.ids = {};
  }
}

export const archivedChannelsState = new ArchivedChannelsState();

// The one "hidden from MY sidebar" predicate: sidebar, Home and unread-selectors.ts all read it.
export function isArchivedForMe(channelId: string): boolean {
  return archivedChannelsState.has(channelId);
}

// ─── Singleton instances ──────────────────────────────────────────

export const coreClient = new SlackClient();
export const authState = new AuthState();
export const connectionState = new ConnectionState();
export const daemonHealthState = new DaemonHealthState();
export const presenceState = new PresenceState();
export const workspaceUserStatusesState = new WorkspaceUserStatusesState();
export const workspaceState = new WorkspaceState();
export const channelState = new ChannelState();
export const dmState = new DmState();
export const agentDmReadState = new AgentDmReadState();

// The frozen "New" cursor of ANY DM being opened (human or agent), read before the open
// marks it read. Gated on the unread COUNT, the cross-device truth: 0 ⇒ null ⇒ the bottom.
// Unreads with no read row yet: the server counted from epoch 0 (getDmUnreadCounts), so the line does too.
export function dmUnreadCursorAtOpen(wsId: string, peerId: string): number | null {
  return notificationState.getCount(wsId, peerId) > 0 ? (readCursorState.getDm(peerId) ?? 0) : null; // null: no divider
}
// The agent DM's "New" cursor for the open's first read, frozen or about to be (the read may
// start before the route freezes it); null: no divider, or the read cursors are not seeded yet.
export function agentDmOpenCursor(wsId: string, peerId: string): number | null {
  if (agentDmReadState.peerId === peerId) return agentDmReadState.unreadCursor;
  return readCursorState.dmSeeded ? dmUnreadCursorAtOpen(wsId, peerId) : null; // null: unseeded
}
// The open's cursor is still to come: not frozen yet, and unreads (with or without a cursor) or unseeded.
export function agentDmCursorUndecided(wsId: string, peerId: string): boolean {
  if (agentDmReadState.peerId === peerId) return false;
  return !readCursorState.dmSeeded || notificationState.getCount(wsId, peerId) > 0;
}
export const userStatusState = new UserStatusState();
export const notificationState = new NotificationState();
export const unreadFlagState = new UnreadFlagState();
export const dmActivityState = new DmActivityState();
export { ReadCursorState };
export const readCursorState = new ReadCursorState();

// ─── Shared DM-list ordering (#17) ────────────────────────────────
// The ONE place that orders DM peers, used by BOTH the sidebar "Direct
// Messages" section and the mobile DMs tab so they can never diverge. Sorts by
// last-message activity DESC (most recent first, Slack/Mattermost-style); peers
// with no recorded activity fall to the bottom. Array.prototype.sort is stable
// in modern JS, so ties keep their incoming order — callers pre-sort by name to
// make the no-activity tail deterministic.
export function sortMembersByDmRecency<T extends { userId: string }>(
  members: readonly T[],
  workspaceId: string | null | undefined,
): T[] {
  if (!workspaceId) return [...members];
  return [...members].sort(
    (a, b) =>
      dmActivityState.getActivity(workspaceId, b.userId) -
      dmActivityState.getActivity(workspaceId, a.userId),
  );
}

// Scroll-to + flash-highlight a specific message (e.g. opening a thread from the
// global Threads view jumps the channel to its root). `nonce` bumps so the same
// id re-triggers the scroll. The timeline surfaces (MessageList, the DM list)
// consume `timelineId`; ThreadPanel consumes a focus scoped to its root
// (`inThread`, knowledge/117) — one store, two exclusive owners.
export class MessageFocusState {
  id: string | null = $state(null);
  // The thread root the target reply lives under; null = a timeline row.
  inThread: string | null = $state(null);
  // A thread reply's root, still pending in the timeline: the channel lands where the thread starts.
  threadRoot: string | null = $state(null);
  nonce = $state(0);
  // The pending target of the channel / DM timeline.
  get timelineId(): string | null {
    return this.threadRoot ?? (this.inThread ? null : this.id);
  }
  focus(id: string): void {
    this.id = id;
    this.inThread = null;
    this.threadRoot = null;
    this.nonce += 1;
  }
  focusInThread(rootId: string, replyId: string): void {
    // Re-arming the jump still in flight keeps what already landed: a root the timeline
    // reached is not scrolled to a second time while the reply lands in the thread.
    if (this.id === replyId && this.inThread === rootId) {
      this.nonce += 1;
      return;
    }
    this.id = replyId;
    this.inThread = rootId;
    this.threadRoot = rootId;
    this.nonce += 1;
  }
  clear(): void {
    this.id = null;
    this.inThread = null;
    this.threadRoot = null;
  }
  // The timeline's jump ended. A thread root is context, so its miss is not announced.
  settleTimeline(found: boolean): void {
    if (this.threadRoot !== null) {
      this.threadRoot = null;
      return;
    }
    // The root already landed: a second timeline loop on the same jump (an agent DM can
    // mount two conversation lists) must not clear the reply the thread pane still owns.
    if (this.inThread !== null) return;
    this.settle(found);
  }
  // The thread pane's jump ended; the timeline's root jump runs on.
  settleThread(found: boolean): void {
    this.id = null;
    this.inThread = null;
    if (!found) toast.error("That message could not be found.");
  }
  // Natural end of a jump. A miss is SAID (Mattermost's "Message Not Found"), never
  // a silent clear that lands the reader wherever the last page left them.
  settle(found: boolean): void {
    this.clear();
    if (!found) toast.error("That message could not be found.");
  }
}
export const messageFocusState = new MessageFocusState();

// Foreground/visibility resume signal. `onForeground()` (app.svelte.ts) bumps this
// on the existing window `focus` / `visibilitychange→visible` / `online` handlers —
// NO new listeners. The active message surface (MessageList, AgentStreamView) watches
// `nonce` and calls its ScrollAnchorController.onForeground(), so returning to the app
// preserves a detached read instead of jumping to the latest message.
export class ForegroundSignalState {
  nonce = $state(0);
  bump(): void {
    this.nonce += 1;
  }
}
export const foregroundSignal = new ForegroundSignalState();

// ─── Threads (CRT) — global followed-threads list + aggregate unread badge ──
// Mirrors NotificationState's delta approach: the list comes from fetch_threads,
// the aggregate counts are seeded once (thread_counts) then maintained by
// applying prev/new deltas from WS events — no refetch per event.
export interface ThreadUnread {
  unreadReplies: number;
  unreadMentions: number;
}
// One page of the Threads feed, and the most the feed's window may grow to (mirrors the
// relay's THREAD_SNAPSHOT_MAX).
export const THREADS_PAGE = 50;
export const THREADS_WINDOW_MAX = 1000;

export class ThreadsState {
  list: ThreadSummary[] = $state([]);
  counts: ThreadCounts = $state({ totalUnreadThreads: 0, totalUnreadMentions: 0 });
  loading = $state(false);
  loadingMore = $state(false);
  fetched = $state(false);
  // The last snapshot came back full (as many rows as the window asked for) and the
  // window can still grow, so older threads exist beyond the loaded list.
  hasMore = $state(false);
  // How many threads the canonical snapshot asks for. Starts at one page and grows with
  // "Load older threads", up to THREADS_WINDOW_MAX — so a refresh after paging keeps
  // every page the viewer opened instead of truncating to the first one.
  windowSize = $state(THREADS_PAGE);
  // A "Mark all read" request is in flight (the button disables meanwhile).
  bulkPending = $state(false);
  // The last snapshot request failed (the pane shows a retry).
  loadError = $state(false);

  // Per-thread "New replies" divider cursor (#7), keyed by root id → the viewer's
  // last_viewed (epoch ms) captured at thread-open from the fetch_thread response.
  // FROZEN: the divider reads this snapshot, NOT the live cursor, so mark_thread_read
  // advancing the server cursor on open doesn't make the divider vanish. ThreadPanel
  // renders UnreadDivider above the first reply with createdAt > this value.
  perThreadLastViewed: Record<string, number> = $state({});

  // Roots the viewer read locally and the relay has not answered for yet, each owned by
  // the read OPERATION (its generation) that placed it: an older read of the same root
  // settling must not lift a newer read's overlay. The list and the totals are
  // otherwise ONE canonical snapshot (applySnapshot); a snapshot fetched before such a
  // read committed still says "unread", so the overlay keeps those rows read until the
  // owning read settles AND a snapshot requested after that answer has applied.
  private overlay = new Map<string, number>();
  // rootId → the fetch number a lifting snapshot must exceed (settleOverlay).
  private settling = new Map<string, number>();

  setPerThreadLastViewed(rootId: string, lastViewed: number): void {
    this.perThreadLastViewed = { ...this.perThreadLastViewed, [rootId]: lastViewed };
  }

  getPerThreadLastViewed(rootId: string): number | null {
    return this.perThreadLastViewed[rootId] ?? null;
  }

  // Replace list and totals with the relay's snapshot (the window + totals from one
  // transaction), then re-apply the overlay. `fetchNo` is the sequencer's fetch counter
  // when this snapshot's request started; a settled read is lifted only by a snapshot
  // requested after its answer.
  applySnapshot(
    s: { threads: ThreadSummary[]; counts: ThreadCounts },
    requested: number,
    fetchNo = Number.POSITIVE_INFINITY,
  ): void {
    for (const [rootId, afterFetch] of Array.from(this.settling.entries())) {
      if (fetchNo > afterFetch) {
        this.overlay.delete(rootId);
        this.settling.delete(rootId);
      }
    }
    this.loadError = false;
    const counts = { ...s.counts };
    const list = s.threads.map((t) => {
      if (!t.root || !this.overlay.has(t.root.id)) return t;
      if (t.unreadReplies > 0 || t.unreadMentions > 0) {
        counts.totalUnreadThreads = Math.max(0, counts.totalUnreadThreads - 1);
        counts.totalUnreadMentions = Math.max(0, counts.totalUnreadMentions - t.unreadMentions);
      }
      return Object.assign({}, t, { unreadReplies: 0, unreadMentions: 0 });
    });
    this.list = list;
    this.counts = counts;
    this.hasMore = s.threads.length >= requested && requested < THREADS_WINDOW_MAX;
    this.fetched = true;
  }

  // "Load older threads": widen the window by one page (bounded). The caller then asks
  // for the canonical snapshot again — pagination goes through the same serialized
  // request as every refresh, so a page and a refresh cannot race.
  growWindow(): boolean {
    if (this.windowSize >= THREADS_WINDOW_MAX) return false;
    this.windowSize = Math.min(THREADS_WINDOW_MAX, this.windowSize + THREADS_PAGE);
    return true;
  }

  // Optimistic: the row reads as read at once and the totals move by what the row is
  // known to hold — the only local knowledge. The overlay is owned by `generation`; a
  // newer read of the same root takes the ownership over.
  readLocally(rootId: string, generation: number): void {
    const owned = this.overlay.has(rootId);
    this.overlay.set(rootId, generation);
    this.settling.delete(rootId);
    if (owned) return;
    const idx = this.list.findIndex((t) => t.root?.id === rootId);
    if (idx < 0) return;
    const row = this.list[idx];
    if (row.unreadReplies <= 0 && row.unreadMentions <= 0) return;
    this.counts = {
      totalUnreadThreads: Math.max(0, this.counts.totalUnreadThreads - 1),
      totalUnreadMentions: Math.max(0, this.counts.totalUnreadMentions - row.unreadMentions),
    };
    const updated = Object.assign({}, row, { unreadReplies: 0, unreadMentions: 0 });
    this.list = [...this.list.slice(0, idx), updated, ...this.list.slice(idx + 1)];
  }

  // A read on another device, pushed with this root's absolute unread and the canonical
  // totals: patch the one row (CYBORG-1201). False when a local read owns the row.
  applyThreadRead(rootId: string, unread: ThreadUnread, counts: ThreadCounts): boolean {
    if (this.overlay.has(rootId)) return false;
    const idx = this.list.findIndex((t) => t.root?.id === rootId);
    if (idx >= 0) {
      const updated = Object.assign({}, this.list[idx], unread);
      this.list = [...this.list.slice(0, idx), updated, ...this.list.slice(idx + 1)];
    }
    this.counts = { ...counts };
    return true;
  }

  // The relay answered the read `generation` (confirmed or refused). Only the owner can
  // settle; and only a snapshot whose request started after this moment (fetch number
  // > `afterFetch`) lifts the overlay — one requested earlier predates the commit.
  settleOverlay(rootId: string, generation: number, afterFetch: number): void {
    if (this.overlay.get(rootId) !== generation) return;
    this.settling.set(rootId, afterFetch);
  }

  // Test seam: who owns this root's overlay, if anyone.
  overlayOwner(rootId: string): number | undefined {
    return this.overlay.get(rootId);
  }

  clear(): void {
    this.list = [];
    this.counts = { totalUnreadThreads: 0, totalUnreadMentions: 0 };
    this.loading = false;
    this.loadingMore = false;
    this.fetched = false;
    this.hasMore = false;
    this.windowSize = THREADS_PAGE;
    this.bulkPending = false;
    this.loadError = false;
    this.perThreadLastViewed = {};
    this.overlay.clear();
    this.settling.clear();
  }
}
export const threadsState = new ThreadsState();

// ─── Saved messages (#609 — personal bookmarks) ───────────────────
// A PRIVATE per-user list of saved messages across channels, shown in the Saved
// view. The list comes from list_saved (newest-saved first); saveMessage toggles
// a row optimistically and the save_message broadcast reconciles across devices.
// Mirrors ThreadsState's list/loading/fetched shape. `savedIds` lets a message row
// anywhere render its filled/empty bookmark without re-fetching the whole list.
export class SavedState {
  list: import("../core/types.js").Message[] = $state([]);
  // The set of message ids the user has saved — drives the per-message toggle
  // glyph. Kept in lockstep with `list` (the Saved view's source of truth) plus
  // any optimistic toggle for a message not currently in the loaded list.
  savedIds: Set<string> = $state(new Set());
  loading = $state(false);
  fetched = $state(false);

  setList(messages: import("../core/types.js").Message[]): void {
    this.list = messages;
    this.savedIds = new Set(messages.map((m) => m.id));
    this.fetched = true;
  }

  isSaved(messageId: string): boolean {
    return this.savedIds.has(messageId);
  }

  // Add/remove a single bookmark. Used for both the optimistic local toggle and
  // the authoritative save_message broadcast. `message` is supplied when known
  // (the toggle has the row in hand) so a save made from a channel can appear in
  // an already-loaded Saved list without a refetch; a broadcast for an unknown
  // message just updates the id set (the list refreshes on next open).
  apply(messageId: string, saved: boolean, message?: import("../core/types.js").Message): void {
    const ids = new Set(this.savedIds);
    if (saved) {
      ids.add(messageId);
      if (message && !this.list.some((m) => m.id === messageId)) {
        // Newest-saved first, mirroring getSavedMessages' ordering.
        this.list = [message, ...this.list];
      }
    } else {
      ids.delete(messageId);
      this.list = this.list.filter((m) => m.id !== messageId);
    }
    this.savedIds = ids;
  }

  clear(): void {
    this.list = [];
    this.savedIds = new Set();
    this.loading = false;
    this.fetched = false;
  }
}
export const savedState = new SavedState();

// ─── Wire client events to state ──────────────────────────────────

coreClient.on("connection", ({ status }) => {
  connectionState.status = status;
});

// Typing expiry pauses while no frame can arrive: the socket is down, or the browser is offline
// (a dropped network leaves the socket "connected" until its heartbeat times out, up to 40 s).
function pauseTypingWhileOffline(status: string): void {
  const online = typeof navigator === "undefined" || navigator.onLine !== false;
  TypingLines.setConnected(status === "connected" && online);
}
// Some test shells stub `window` without listeners.
if (typeof window !== "undefined" && typeof window.addEventListener === "function") {
  const sync = (): void => pauseTypingWhileOffline(connectionState.status);
  window.addEventListener("offline", sync);
  window.addEventListener("online", sync);
}

coreClient.on("channel_message", (msg) => {
  if (msg.channelId === channelState.activeId) {
    channelState.addMessage(msg);
  }
});

coreClient.on("dm", (msg) => {
  if (msg.channelId && msg.channelId === channelState.activeId) {
    channelState.addMessage(msg);
  }
  const myId = authState.user?.id;
  if (myId) {
    const peerId = msg.fromId === myId ? (msg.toId ?? null) : msg.fromId;
    if (peerId && peerId === dmState.activePeerId) {
      dmState.addMessage(msg);
    }
  }
});

coreClient.on("typing", (event) => {
  if (event.channelId === channelState.activeId && event.fromId !== authState.user?.id) {
    if (event.stopped !== true) channelState.addTyping(event);
  }
});

coreClient.on("error", (err) => {
  connectionState.error = err.message;
});

// ─── Core Actions ─────────────────────────────────────────────────

export { getSavedSession } from "./saved-session.js";
export type { SavedSession } from "./saved-session.js";

// The stash the removed superadmin impersonation wrote (CYBORG-1102): an ADMIN's
// own url+token, still sitting in browsers that used the feature. Cleared on
// sign-out so the next human on a shared device cannot boot as that admin.
const IMPERSONATION_STASH_KEY = "cyborg7-superadmin-impersonation";

export async function connectToServer(
  url: string,
  token: string,
  // CYBORG-87: the durable refresh token returned alongside the access token by
  // login/register/refresh. Persisted so a later 4003 (expiry / JWT-secret
  // rotation) self-heals silently instead of a silent session death.
  refreshToken?: string,
): Promise<void> {
  authState.token = token;
  persistDesktopCredential(url, token, refreshToken);
  if (refreshToken) saveRefreshToken(refreshToken);
  await coreClient.connect(url, token);
  const authResp = await coreClient.authenticate();
  authState.user = authResp.user;
  // Memberships PLUS the host workspaces this account may enter as a Cyborg7
  // Connect external, in the one shared builder both connectToServer's use.
  workspaceState.list = workspaceListFromAuth(authResp);
  workspaceState.listLoaded = true;
  daemonHealthState.start(url);
  try {
    localStorage.setItem(SESSION_KEY, JSON.stringify({ url, token }));
  } catch {
    // intentional: best-effort session persistence; storage-disabled just means
    // the user re-auths next launch.
  }
}

export function clearSavedSession(): void {
  // Drop the durable refresh token alongside the session so the next account
  // can't inherit it and a rejected token isn't retried forever (CYBORG-87).
  clearRefreshToken();
  try {
    localStorage.removeItem(SESSION_KEY);
    // …and the leftover impersonation stash, a SECOND set of credentials for this
    // device. Dropped on every path that invalidates the saved session, not only
    // deliberate sign-out, so an admin pass cannot sit in a browser that has
    // already reached /login (CYBORG-1102).
    localStorage.removeItem(IMPERSONATION_STASH_KEY);
  } catch {
    // intentional: best-effort session clear; nothing to surface if storage is gone.
  }
}

export async function selectWorkspace(workspace: Workspace): Promise<void> {
  workspaceState.current = workspace;
  channelState.clear();
  channelState.activeId = null;

  const [channels, tasks] = await Promise.all([
    coreClient.fetchChannels(workspace.id),
    coreClient.fetchTasks(workspace.id),
  ]);

  workspaceState.channels = channels;
  workspaceState.tasks = tasks;

  workspaceState.membersLoading = true;
  coreClient
    .listMembers(workspace.id)
    .then(({ members, external }) => {
      workspaceState.members = members;
      workspaceState.external = external;
      return undefined;
    })
    .catch(() => {
      workspaceState.members = [];
    })
    .finally(() => {
      workspaceState.membersLoading = false;
    });

  if (channels.length > 0) {
    await selectChannel(channels[0].id);
  }
}

export async function selectChannel(channelId: string): Promise<void> {
  if (!workspaceState.current) return;
  channelState.activeId = channelId;
  channelState.loading = true;
  channelState.messages = [];

  const { messages, hasMore } = await coreClient.fetchMessages(
    workspaceState.current.id,
    channelId,
    { limit: 50 },
  );
  channelState.messages = messages;
  channelState.hasMore = hasMore;
  channelState.loading = false;
}

export async function loadMoreMessages(): Promise<void> {
  if (
    !workspaceState.current ||
    !channelState.activeId ||
    !channelState.hasMore ||
    channelState.loading
  )
    return;
  channelState.loading = true;
  const oldest = channelState.messages[0];
  const { messages, hasMore } = await coreClient.fetchMessages(
    workspaceState.current.id,
    channelState.activeId,
    { before: oldest?.id, limit: 50 },
  );
  channelState.prependMessages(messages, hasMore);
  channelState.loading = false;
}

export function sendMessage(text: string, mentions?: string[]): void {
  if (!workspaceState.current || !channelState.activeId) return;
  coreClient.sendMessage(workspaceState.current.id, channelState.activeId, text, mentions);
}

export function sendTypingIndicator(): void {
  if (!workspaceState.current || !channelState.activeId) return;
  coreClient.sendTyping(workspaceState.current.id, channelState.activeId);
}

export function selectDm(peerId: string): void {
  dmState.clear();
  dmState.activePeerId = peerId;
  channelState.activeId = null;
}

export function sendDmMessage(text: string): void {
  if (!workspaceState.current || !dmState.activePeerId) return;
  const peerId = dmState.activePeerId;
  const myId = authState.user?.id ?? "unknown";
  // Per-workspace display name (0121), matching what the server stamps on the echo.
  const myName = authState.selfName ?? authState.user?.email ?? "You";
  coreClient.sendDm(workspaceState.current.id, peerId, text);
  dmState.addMessage({
    id: `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    channelId: null,
    fromId: myId,
    fromType: "human",
    fromName: myName,
    toId: peerId,
    text,
    seq: 0,
    createdAt: Date.now(),
  });
}

export async function inviteMember(
  email: string,
  role: "admin" | "member" | "viewer" = "member",
): Promise<void> {
  if (!workspaceState.current) throw new Error("No workspace selected");
  await coreClient.inviteMember(workspaceState.current.id, email, role);
  const { members } = await coreClient.listMembers(workspaceState.current.id);
  workspaceState.members = members;
}

export async function removeMember(userId: string): Promise<void> {
  if (!workspaceState.current) throw new Error("No workspace selected");
  await coreClient.removeMember(workspaceState.current.id, userId);
  workspaceState.members = workspaceState.members.filter((m) => m.userId !== userId);
}

export async function updateMemberRole(
  userId: string,
  role: "admin" | "member" | "viewer",
): Promise<void> {
  if (!workspaceState.current) throw new Error("No workspace selected");
  await coreClient.updateRole(workspaceState.current.id, userId, role);
  workspaceState.members = workspaceState.members.map((m) =>
    m.userId === userId ? { ...m, role } : m,
  );
}

export function disconnectFromServer(): void {
  clearSavedSession();
  coreClient.disconnect();
  authState.user = null;
  authState.token = null;
  workspaceState.current = null;
  workspaceState.list = [];
  workspaceState.listLoaded = false;
  workspaceState.channels = [];
  workspaceState.tasks = [];
  workspaceState.agents = [];
  channelState.clear();
  daemonHealthState.stop();
  presenceState.clear();
  workspaceUserStatusesState.clear();
  connectionState.status = "disconnected";
}
